import { copyOfflineCacheResponseMeta } from '@tov-plus/sdk';
import type { PublishingDocument } from '@tov-plus/publishing';
import type {
  AssetAttachmentRecord,
  AssetDetail,
  AssetRecord,
  AssetVariantRecord,
  AssetVisibilityRuleRecord,
} from '@tov-plus/assets';
import type {
  AiTaskCatalogResponse,
  AiTaskExecutionRequest,
  AiTaskExecutionResponse,
  ApiEventSummary,
  ApiGuestAccessLifecycle,
  ApiFormSubmissionRecord,
  ApiGalleryAlbumRecord,
  ApiGalleryAlbumItemRecord,
  ApiGalleryCommentRecord,
  ApiGalleryReactionSummary,
  ApiGuestJourneySummary,
  ApiGuestLifecycleStateMachine,
  ApiGuestOnboardingSummary,
  ApiGuestSession,
  ApiGuestTask,
  ApiGuestVisibleForm,
  ApiGalleryUploadSessionRecord,
  ApiRsvpInvitationRecord,
  ApiRsvpResponseRecord,
  GuestRegistryResponse,
  GuestSharedPlanningResponse,
  GuestGalleryResponse,
  GuestGalleryCommentResponse,
  GuestGalleryFavoriteResponse,
  GuestGalleryReactionResponse,
  ApiGallerySharePackageResponse,
  GuestGalleryUploadDeleteResponse,
  GuestGalleryUploadRequest,
  GuestGalleryUploadUpdateRequest,
  RequestMediaUploadUrlResponse,
  CompleteMediaUploadRequest,
  GuestSeatingResponse,
  GuestTimelineResponse,
  GuestExperienceBootstrapResponse,
  GuestRsvpResponse,
  GuestVisibleFormsResponse,
  GuestOccasionContextResponse,
  GuestVisibleLocationsResponse,
  HttpClient,
  OccasionAssetsResponse,
  OccasionEventListResponse,
  ApiUnifiedContextResponse,
} from '@tov-plus/sdk';
import type {
  EventSummary,
  FormSubmissionInput,
  FormSubmissionRecord,
  GalleryAlbumItemRecord,
  GalleryAlbumRecord,
  GalleryItemRecord,
  GuestIdentityContext,
  GuestAccessLifecycleRecord,
  GuestJourneySummaryRecord,
  GuestLifecycleRecord,
  GuestOnboardingSummaryRecord,
  GuestJourneySessionContext,
  GuestTaskRecord,
  UnifiedTaskRecord,
  UnifiedTaskSummaryRecord,
  GuestVisibleFormRecord,
  RegistryClaimRecord,
  RegistryItemRecord,
  RegistryRecord,
  InvitationOpeningExperienceRecord,
  OccasionSummary,
  GuestTheme,
  RsvpResponseStatus,
  RsvpGuestEventState,
  RsvpInvitationAudienceRuleType,
  RsvpInvitationRecord,
  RsvpResponseRecord,
  SharedPlanningBoardRecord,
  SharedPlanningClaimRecord,
  SharedPlanningItemRecord,
  SeatingAssignmentRecord,
  SeatingPlanRecord,
  SeatingTableRecord,
  TimelineItemRecord,
  ResolvedLocalizationContext,
  ResolvedUnifiedContextRecord,
} from '@tov-plus/domain';
import {
  buildGuestTheme,
  normalizeInvitationOpeningExperience,
} from '@tov-plus/domain';

import {
  mapGuestMessagingInboxItem,
  mapGuestMessagingMessage,
  mapGuestMessagingThreadDetail,
  mapGuestSearchResponse,
  mapGuestSupportRequest,
  type GuestMessagingInboxItemResponse,
  type GuestMessagingBodyFormat,
  type GuestMessagingMessageType,
  type GuestMessagingParticipantKind,
  type GuestMessagingMessageResponse,
  type GuestMessagingThreadType,
  type GuestMessagingThreadDetailResponse,
  type GuestSearchSurface,
  type GuestSearchResponseBody,
  type GuestSupportQueueState,
  type GuestSupportRequestPriority,
  type GuestSupportRequestResponse,
  type GuestSupportRequestStatus,
  type GuestSupportResolutionEventType,
  type GuestSupportResolutionState,
} from './guestApiResponseMappers';

const publicGuestAccessSentinel = '__public__';

function galleryGuestTokenQuery(guestToken: string) {
  return guestToken !== publicGuestAccessSentinel
    ? { guest_token: guestToken }
    : {};
}

export interface GuestOccasionContext {
  occasion: OccasionSummary;
  guestDisplayName: string;
  accessMode: 'guest_token' | 'claimed_guest' | 'public_visitor';
  identitySummary: GuestIdentityContext;
  unifiedContext: ResolvedUnifiedContextRecord;
  multiLanguageCapability: {
    key: string;
    enabled: boolean;
    source:
      | 'default'
      | 'package'
      | 'billing_entitlement'
      | 'manual_override'
      | 'internal_flag'
      | null;
    sourceKey: string | null;
    reason:
      | 'default_enabled'
      | 'package_assignment'
      | 'billing_entitlement'
      | 'manual_override'
      | 'internal_flag'
      | 'not_enabled';
  };
  localizationContext: ResolvedLocalizationContext;
}

export interface GuestPublishableInvitation {
  id: string;
  name: string;
  publishingType: string;
  width: number;
  height: number;
  document: PublishingDocument;
  imageUrlByAssetId: Record<string, string | null>;
}

type ApiGuestPublishableInvitation = {
  id: string;
  name: string;
  publishing_type: string;
  width: number;
  height: number;
  document: PublishingDocument;
  image_url_by_asset_id: Record<string, string | null>;
};

const RSVP_INVITATION_AUDIENCE_RULE_TYPES = new Set<string>([
  'manual_selection',
  'all_event_guests',
  'anyone_with_link',
  'filter',
]);

function isRsvpInvitationAudienceRuleType(
  value: string,
): value is RsvpInvitationAudienceRuleType {
  return RSVP_INVITATION_AUDIENCE_RULE_TYPES.has(value);
}

export interface GuestExperienceBootstrap {
  context: GuestOccasionContext;
  openingExperience: InvitationOpeningExperienceRecord | null;
  designTheme: GuestTheme | null;
  eventVisualOverrides: GuestExperienceBootstrapResponse['event_visual_overrides'];
  tokenInheritance: GuestExperienceBootstrapResponse['token_inheritance'];
  guestWebsite: GuestExperienceBootstrapResponse['guest_website'];
  events: EventSummary[];
  forms: GuestVisibleFormRecord[];
  recentCommunications: GuestRecentCommunication[];
  journeySummary: GuestJourneySummaryRecord;
  guestSession: GuestJourneySessionContext;
  onboardingSummary: GuestOnboardingSummaryRecord;
  accessLifecycle: GuestAccessLifecycleRecord;
  guestStateMachine: GuestLifecycleRecord;
  guestIntelligence: GuestIntelligenceSummary | null;
  guestIntelligenceSnapshots: GuestIntelligenceSnapshot[];
  taskSummary: UnifiedTaskSummaryRecord;
  unifiedTasks: UnifiedTaskRecord[];
  tasks: GuestTaskRecord[];
}

export interface GuestIntelligenceInsight {
  key: string;
  severity: 'critical' | 'important' | 'normal' | 'informational';
  title: string;
  summary: string;
  recommendedAction: string;
  explanation: string;
  explanationKey?: NonNullable<
    GuestExperienceBootstrapResponse['guest_intelligence']
  >['insights'][number]['explanation_key'];
  sourceSummary: string;
  sourceSummaryKey?: NonNullable<
    GuestExperienceBootstrapResponse['guest_intelligence']
  >['insights'][number]['source_summary_key'];
  target: GuestJourneySummaryRecord['recommendedTarget'];
  href: string;
  sourceCount: number;
  userState: 'active' | 'acknowledged' | 'dismissed';
  userStateUpdatedAt: string | null;
}

export interface GuestIntelligenceSummary {
  status: 'action_required' | 'ready' | 'watch';
  headline: string;
  summary: string;
  confidence: 'high' | 'medium' | 'low';
  nextBestTarget: GuestJourneySummaryRecord['recommendedTarget'];
  nextBestHref: string;
  insights: GuestIntelligenceInsight[];
  dataQuality: {
    visibleSurfaceCount: number;
    personalizedSignalCount: number;
    summary: string;
  };
  freshnessWarnings: string[];
  freshnessWarningKeys?: NonNullable<
    GuestExperienceBootstrapResponse['guest_intelligence']
  >['freshness_warning_keys'];
}

export interface GuestIntelligenceSnapshot {
  id: string;
  occasionId: string;
  actorId: string | null;
  generatedAt: string;
  status: GuestIntelligenceSummary['status'];
  confidence: GuestIntelligenceSummary['confidence'];
  headline: string;
  summary: string;
  sourceKeys: string[];
  sourceHash: string;
  payload: GuestIntelligenceSummary;
  createdAt: string;
}

export interface GuestRecentCommunication {
  delivery: {
    id: string;
    status: string;
    recipientDisplayName: string;
    targetAddress: string | null;
    queuedAt: string;
    deliveredAt: string | null;
  };
  message: {
    id: string;
    subjectText: string | null;
    bodyText: string;
    communicationEventKey: string;
  } | null;
  event: EventSummary | null;
  deepLink: string | null;
}

const guestOfflineStaleAfterMs = 24 * 60 * 60 * 1_000;

function guestOfflineCache(
  occasionSlug: string,
  guestToken: string,
  key: string,
) {
  return {
    key: `guest:${occasionSlug}:${key}`,
    scope: {
      app: 'guests' as const,
      guestToken,
    },
    staleAfterMs: guestOfflineStaleAfterMs,
    allowStaleWhenOffline: true,
  };
}

export interface GuestGalleryViewItem {
  galleryItem: GalleryItemRecord;
  asset: AssetRecord;
  assetUrl: string | null;
  variants: AssetVariantRecord[];
  displayUrl: string | null;
  downloadUrl: string;
  event: EventSummary | null;
  reactions: ApiGalleryReactionSummary[];
  comments: ApiGalleryCommentRecord[];
}

export interface GuestGalleryAlbum {
  album: GalleryAlbumRecord;
  isOpen: boolean;
  items: Array<{
    albumItem: GalleryAlbumItemRecord;
    galleryItem: GalleryItemRecord;
  }>;
}

export interface GuestGalleryUploadSession {
  id: string;
  eventId: string | null;
  albumId: string | null;
  fileName: string;
  mimeType: string;
  fileSizeBytes: number | null;
  caption: string | null;
  signedUploadUrl: string;
  uploadHeaders: Record<string, string>;
  status:
    | 'pending_upload'
    | 'uploaded'
    | 'processing'
    | 'completed'
    | 'failed'
    | 'expired';
  expiresAt: string;
}

export interface GuestTimelineViewItem {
  timelineItem: TimelineItemRecord;
  event: EventSummary;
}

export interface GuestSeatingViewItem {
  seatingPlan: SeatingPlanRecord;
  table: SeatingTableRecord;
  assignment: SeatingAssignmentRecord;
  event: EventSummary;
}

export interface GuestSharedPlanningViewItem {
  board: SharedPlanningBoardRecord;
  event: EventSummary | null;
  items: Array<{
    item: SharedPlanningItemRecord;
    myClaim: SharedPlanningClaimRecord | null;
    progress: {
      claimantCount: number;
      activeClaimCount: number;
      quantityNeeded: number | null;
      quantityClaimed: number | null;
      quantityRemaining: number | null;
      fulfilledClaimCount: number;
    };
  }>;
}

export interface GuestRegistryViewItem {
  item: RegistryItemRecord;
  myClaim: RegistryClaimRecord | null;
  analytics: {
    claimedQuantity: number;
    remainingQuantity: number;
    activeClaimCount: number;
    claimantCount: number;
    fullyClaimed: boolean;
  };
}

export interface GuestVisibleLocation {
  id: string;
  occasionId: string;
  parentLocationId: string | null;
  type:
    | 'venue'
    | 'parking'
    | 'transportation_point'
    | 'accommodation'
    | 'point_of_interest'
    | 'internal_venue_point'
    | 'custom';
  title: string;
  description: string | null;
  addressText: string | null;
  latitude: number | null;
  longitude: number | null;
  mapLabel: string | null;
  venueMapAssetId: string | null;
  typeProps: Record<string, unknown>;
}

export interface GuestCommunicationPreferences {
  occasionId: string;
  actorId: string | null;
  preferredLocaleCode: string | null;
  notificationPreferences: Array<{
    id: string;
    channelType: 'email' | 'sms' | 'whatsapp' | 'push' | 'in_app' | 'webhook';
    isEnabled: boolean;
    preferredLocaleCode: string | null;
    updatedAt: string;
  }>;
}

export interface GuestDraftState {
  occasionId: string;
  actorId: string | null;
  rsvpDraft: Record<string, unknown> | null;
  rsvpDraftUpdatedAt: string | null;
  formDrafts: Record<string, unknown> | null;
  formDraftsUpdatedAt: string | null;
  supportRequests: Record<string, unknown> | null;
  supportRequestsUpdatedAt: string | null;
}

export interface GuestMessagingThreadSummary {
  id: string;
  eventId: string | null;
  subject: string | null;
  guestThreadType: GuestMessagingThreadType;
  guestReplyAllowed: boolean;
  updatedAt: string;
  lastMessageAt: string | null;
}

export interface GuestMessagingMessage {
  id: string;
  body: string;
  bodyFormat: GuestMessagingBodyFormat;
  messageType: GuestMessagingMessageType;
  senderDisplayName: string;
  createdAt: string;
  editedAt: string | null;
  deletedAt: string | null;
  attachmentCount: number;
}

export interface GuestMessagingInboxItem {
  thread: GuestMessagingThreadSummary;
  latestMessage: GuestMessagingMessage | null;
  participants: Array<{
    displayName: string;
    participantKind: GuestMessagingParticipantKind;
  }>;
  unreadCount: number;
  lastActivityAt: string;
  latestMessagePreview: string | null;
}

export interface GuestMessagingThreadDetail {
  thread: GuestMessagingThreadSummary;
  participants: GuestMessagingInboxItem['participants'];
  messages: GuestMessagingMessage[];
  pageSize: number;
  nextBefore: string | null;
}

export interface GuestMessagingNotificationPreferences {
  guestEmailEnabled: boolean;
  guestSmsEnabled: boolean;
  guestWhatsappEnabled: boolean;
  guestPushEnabled: boolean;
  inAppMessagesEnabled: boolean;
  contactMethods: {
    email: string | null;
    sms: string | null;
    whatsapp: string | null;
    pushEnabled: boolean;
  };
}

export interface GuestSupportRequest {
  id: string;
  actorId: string;
  actorDisplayName: string;
  actorType: string;
  distributionId: string | null;
  invitationId: string | null;
  question: string;
  route: string;
  status: GuestSupportRequestStatus;
  priority: GuestSupportRequestPriority;
  queueState: GuestSupportQueueState;
  assignedOwner: string | null;
  escalationOwner: string | null;
  acknowledgedAt: string | null;
  resolvedAt: string | null;
  slaDueAt: string | null;
  resolutionState: GuestSupportResolutionState;
  replies: Array<{
    id: string;
    body: string;
    author: string;
    sentAt: string;
  }>;
  resolutionEvents: Array<{
    id: string;
    type: GuestSupportResolutionEventType;
    body: string;
    createdAt: string;
  }>;
  createdAt: string;
  updatedAt: string;
  metadata: Record<string, unknown>;
}

export interface GuestSupportInbox {
  occasionId: string;
  actorId: string | null;
  items: GuestSupportRequest[];
  summary: {
    totalCount: number;
    openCount: number;
    resolvedCount: number;
  };
}

export interface GuestSecureResumePacket {
  occasionSlug: string;
  actorId: string;
  resumeCode: string;
  resumePath: string | null;
  createdAt: string;
  expiresAt: string;
}

export interface GuestReviewerPreview {
  occasionSlug: string;
  occasionDisplayName: string;
  previewMode:
    | 'public_landing'
    | 'invited_guest'
    | 'rsvp_pending'
    | 'rsvp_complete'
    | 'event_day';
  viewport: 'desktop' | 'mobile';
  expiresAt: string;
  websiteIdentity: {
    siteTitleOverride: string | null;
    subtitleTagline: string | null;
  };
  sections: Array<{
    id: string;
    type: string;
    sectionKey: string;
    anchorLabel: string | null;
    contentPayload: Record<string, unknown>;
  }>;
  navItems: Array<{
    id: string;
    label: string;
    targetSectionId: string | null;
    targetPath: string | null;
  }>;
}

function mapRegistry(
  response: GuestRegistryResponse['registry'],
): RegistryRecord {
  return {
    id: response.id,
    occasionId: response.occasion_id,
    installationId: response.installation_id,
    moduleKey: response.module_key,
    visibility: response.visibility,
    createdByUserId: response.created_by_user_id,
    createdAt: response.created_at,
    updatedAt: response.updated_at,
  };
}

function mapRegistryItem(
  response: GuestRegistryResponse['items'][number]['item'],
): RegistryItemRecord {
  return {
    id: response.id,
    registryId: response.registry_id,
    occasionId: response.occasion_id,
    name: response.name,
    description: response.description,
    link: response.link,
    price: response.price,
    currency: response.currency,
    quantity: response.quantity,
    claimMode: response.claim_mode,
    status: response.status,
    sortOrder: response.sort_order,
    visibleToGuests: response.visible_to_guests,
    external: response.external,
    createdAt: response.created_at,
    updatedAt: response.updated_at,
    archivedAt: response.archived_at,
  };
}

function mapGuestVisibleLocation(
  location: GuestVisibleLocationsResponse['locations'][number],
): GuestVisibleLocation {
  return {
    id: location.id,
    occasionId: location.occasion_id,
    parentLocationId: location.parent_location_id,
    type: location.type,
    title: location.guest_facing_title_override ?? location.title,
    description: location.guest_facing_summary_override ?? location.description,
    addressText: location.address_text,
    latitude: location.latitude,
    longitude: location.longitude,
    mapLabel: location.map_label,
    venueMapAssetId: location.venue_map_asset_id,
    typeProps: location.type_props,
  };
}

function mapRegistryClaim(
  response: GuestRegistryResponse['items'][number]['my_claim'],
): RegistryClaimRecord | null {
  if (!response) {
    return null;
  }

  return {
    id: response.id,
    itemId: response.item_id,
    occasionId: response.occasion_id,
    actorId: response.actor_id,
    quantity: response.quantity,
    status: response.status,
    confirmationNote: response.confirmation_note,
    createdAt: response.created_at,
    updatedAt: response.updated_at,
    releasedAt: response.released_at,
  };
}

function mapGuestRegistryViewItem(
  response: GuestRegistryResponse['items'][number],
): GuestRegistryViewItem {
  return {
    item: mapRegistryItem(response.item),
    myClaim: mapRegistryClaim(response.my_claim),
    analytics: {
      claimedQuantity: response.analytics.claimed_quantity,
      remainingQuantity: response.analytics.remaining_quantity,
      activeClaimCount: response.analytics.active_claim_count,
      claimantCount: response.analytics.claimant_count,
      fullyClaimed: response.analytics.fully_claimed,
    },
  };
}

function mapOccasion(response: GuestOccasionContextResponse['occasion']) {
  const identityImage =
    response.identity_image ??
    ('avatar_asset_id' in response.settings &&
    typeof response.settings.avatar_asset_id === 'string' &&
    response.settings.avatar_asset_id.length > 0
      ? {
          type: 'uploaded_image' as const,
          asset_id: response.settings.avatar_asset_id,
        }
      : {
          type: 'initials' as const,
          initials_mode: 'automatic' as const,
          background_color: 'hsl(12 62% 84%)' as const,
        });

  return {
    id: response.id,
    slug: response.slug,
    displayName: response.display_name,
    identityImage: mapIdentityImageConfig(identityImage),
    identityImageUrl: response.identity_image_url ?? null,
    description: response.description ?? null,
    welcomeMessage: response.welcome_message ?? null,
    occasionType: response.occasion_type,
    status: response.status,
    defaultLocaleCode: response.default_locale_code,
    resolvedLocaleCode: response.resolved_locale_code ?? null,
    timezone: response.timezone,
    settings: {
      guestPathMode: response.settings.guest_path_mode,
    },
    createdAt: response.created_at,
    updatedAt: response.updated_at,
  } satisfies OccasionSummary;
}

function mapEvent(response: ApiEventSummary) {
  const identityImage =
    response.identity_image ??
    ('cover_image_asset_id' in response &&
    typeof response.cover_image_asset_id === 'string' &&
    response.cover_image_asset_id.length > 0
      ? {
          type: 'uploaded_image' as const,
          asset_id: response.cover_image_asset_id,
        }
      : {
          type: 'initials' as const,
          initials_mode: 'automatic' as const,
          background_color: 'hsl(12 62% 84%)' as const,
        });

  return {
    id: response.id,
    occasionId: response.occasion_id,
    eventKey: response.event_key,
    displayName: response.display_name,
    identityImage: mapIdentityImageConfig(identityImage),
    identityImageUrl: response.identity_image_url ?? null,
    eventType: response.event_type,
    description: response.description,
    guestSummary: response.guest_summary,
    resolvedLocaleCode: response.resolved_locale_code ?? null,
    status: response.status,
    visibility: response.visibility,
    startsAt: response.starts_at,
    endsAt: response.ends_at,
    timezone: response.timezone,
    locationName: response.location_name,
    locationAddress: response.location_address,
    sortOrder: response.sort_order,
    createdAt: response.created_at,
    updatedAt: response.updated_at,
  } satisfies EventSummary;
}

function mapIdentityImageConfig(
  response: ApiEventSummary['identity_image'],
): EventSummary['identityImage'] {
  if (response.type === 'initials') {
    return {
      type: 'initials',
      initialsMode: response.initials_mode,
      initials: response.initials,
      backgroundColor: response.background_color,
    };
  }

  if (response.type === 'icon') {
    return {
      type: 'icon',
      iconKey: response.icon_key,
      backgroundColor: response.background_color,
    };
  }

  return {
    type: 'uploaded_image',
    assetId: response.asset_id,
  };
}

function mapLocalizationContext(
  response: GuestOccasionContextResponse['localization_context'],
) {
  return {
    requestedLocaleCode: response.requested_locale_code,
    preferredLocaleCode: response.preferred_locale_code,
    resolvedLocaleCode: response.resolved_locale_code,
    localeSource: response.locale_source,
    fallbackLocaleCodes: response.fallback_locale_codes,
  } satisfies ResolvedLocalizationContext;
}

function mapUnifiedContext(
  response: ApiUnifiedContextResponse,
): ResolvedUnifiedContextRecord {
  return {
    occasionId: response.occasion_id,
    audience: response.audience,
    consumer: response.consumer,
    roleView: response.role_view,
    visibilityScope: response.visibility_scope,
    primaryContextId: response.primary_context.context_id,
    primaryContextType: response.primary_context.context_type,
    primaryContextLabel: response.primary_context.label,
    activeContextIds: response.active_context_ids,
    activeEventIds: response.active_event_ids,
    visibleEventIds: response.visible_event_ids,
    activeActorId: response.active_actor_id,
    visibleActorIds: response.visible_actor_ids,
    moduleScope: response.module_scope.map((item) => ({
      moduleKey: item.module_key,
      status: item.status,
      reason: item.reason,
      installationId: item.installation_id,
      relevantEventIds: item.relevant_event_ids,
      lifecycleStage: item.lifecycle_stage,
      availability: item.availability,
      audienceVisible: item.audience_visible,
      readOnly: item.read_only,
      capabilityMode: item.capability_mode,
    })),
    capabilityScope: response.capability_scope.map((capability) => ({
      capabilityKey:
        capability.key as ResolvedUnifiedContextRecord['capabilityScope'][number]['capabilityKey'],
      enabled: capability.enabled,
      reason: capability.enabled ? 'capability_enabled' : 'capability_disabled',
      source: capability.source,
      sourceKey: capability.source_key,
    })),
    candidateActionScope: {
      occasionId: response.candidate_action_scope.occasion_id,
      audience: response.candidate_action_scope.audience,
      eventIds: response.candidate_action_scope.event_ids,
      actorIds: response.candidate_action_scope.actor_ids,
    },
    sources: response.sources.map((source) => ({
      sourceType: source.source_type,
      sourceKey: source.source_key,
      sourceValue: source.source_value,
      precedence: source.precedence,
      applied: source.applied,
      notes: source.notes,
    })),
    warnings: response.warnings,
    resolutionPath: response.resolution_path.map((step) => ({
      stepKey: step.step_key,
      summary: step.summary,
      metadata: step.metadata,
    })),
  };
}

function mapJourneySummary(
  response: ApiGuestJourneySummary,
): GuestJourneySummaryRecord {
  return {
    phase: response.phase,
    summary: response.summary,
    recommendedTarget: response.recommended_target,
    recommendedLabel: response.recommended_label,
    attentionTarget: response.attention_target,
    availableTargets: response.available_targets,
    returnTarget: response.return_target,
    visibleEventCount: response.visible_event_count,
    visibleFormCount: response.visible_form_count,
    invitedEventCount: response.invited_event_count,
    pendingRsvpEventCount: response.pending_rsvp_event_count,
    pendingFormCount: response.pending_form_count,
    registryItemCount: response.registry_item_count,
    visibleScheduleCount: response.visible_schedule_count,
    visibleSeatingCount: response.visible_seating_count,
    galleryItemCount: response.gallery_item_count,
    galleryUploadAllowed: response.gallery_upload_allowed,
    sharedPlanningItemCount: response.shared_planning_item_count,
  };
}

function mapGuestTheme(
  response: GuestExperienceBootstrapResponse['design_theme'],
): GuestTheme | null {
  if (!response) {
    return null;
  }

  return buildGuestTheme({
    occasionId: response.occasion_id,
    themeId: response.id,
    themeName: response.theme_name,
    version: response.version,
    baseThemeKey: response.base_theme_key,
    styleVariant: response.style_variant,
    presentationTone: response.model.presentation_tone,
    tokens: response.tokens,
    assets: {
      logo: {
        assetId: response.assets.logo_asset_id,
        url: response.assets.logo_asset_url,
      },
      mark: {
        assetId: response.assets.mark_asset_id,
        url: response.assets.mark_asset_url,
      },
      hero: {
        assetId: response.assets.hero_asset_id,
        url: response.assets.hero_asset_url,
      },
      decorative: {
        assetId: response.assets.decorative_asset_id,
        url: response.assets.decorative_asset_url,
      },
    },
    templates: {
      invitation: response.templates.invitation,
      signage: response.templates.signage,
      guestWeb: response.templates.guest_web,
    },
  });
}

function mapGuestSession(
  response: ApiGuestSession,
): GuestJourneySessionContext {
  return {
    actorId: response.actor_id,
    occasionId: response.occasion_id,
    activeEventIds: response.active_event_ids,
    roleView: response.role_view,
    accessMode: response.access_mode,
    responseScope: response.response_scope,
    responseUnitActorId: response.response_unit_actor_id,
    responseUnitDisplayName: response.response_unit_display_name,
  };
}

function mapGuestOnboardingSummary(
  response: ApiGuestOnboardingSummary,
): GuestOnboardingSummaryRecord {
  return {
    status: response.status,
    summary: response.summary,
    responseScope: response.response_scope,
    participantCount: response.participant_count,
    delegateCount: response.delegate_count,
    householdCount: response.household_count,
    localeCode: response.locale_code,
  };
}

function mapGuestAccessLifecycle(
  response: ApiGuestAccessLifecycle,
): GuestAccessLifecycleRecord {
  return {
    status: response.status,
    expiresAt: response.expires_at,
    revokedAt: response.revoked_at,
    summary: response.summary,
  };
}

function mapGuestLifecycleStateMachine(
  response: ApiGuestLifecycleStateMachine,
): GuestLifecycleRecord {
  return {
    machineKey: response.machine_key,
    occasionId: response.occasion_id,
    actorId: response.actor_id,
    currentState: response.current_state,
    readiness: response.readiness,
    summary: response.summary,
    readinessSummary: response.readiness_summary,
    progressPercent: response.progress_percent,
    hasPendingRequiredActions: response.has_pending_required_actions,
    requiredActionCount: response.required_action_count,
    recommendedTransitionKey: response.recommended_transition_key,
    currentStateEnteredAt: response.current_state_entered_at,
    lastTransitionAt: response.last_transition_at,
    lastEvaluatedAt: response.last_evaluated_at,
    transitionCount: response.transition_count,
    latestTransition: response.latest_transition
      ? {
          transitionKey: response.latest_transition.transition_key,
          fromState: response.latest_transition.from_state,
          toState: response.latest_transition.to_state,
          triggerKey: response.latest_transition.trigger_key,
          summary: response.latest_transition.summary,
          transitionType: response.latest_transition.transition_type,
          occurredAt: response.latest_transition.occurred_at,
        }
      : null,
  };
}

function mapGuestTask(response: ApiGuestTask): GuestTaskRecord {
  return {
    type: response.type,
    status: response.status,
    required: response.required,
    target: response.target,
    label: response.label,
    summary: response.summary,
    metricLabel: response.metric_label,
    metricValue: response.metric_value,
  };
}

function mapGuestIntelligence(
  response: GuestExperienceBootstrapResponse['guest_intelligence'],
): GuestIntelligenceSummary | null {
  if (!response) {
    return null;
  }

  return {
    status: response.status,
    headline: response.headline,
    summary: response.summary,
    confidence: response.confidence,
    nextBestTarget: response.next_best_target,
    nextBestHref: response.next_best_href,
    insights: response.insights.map((insight) => ({
      key: insight.key,
      severity: insight.severity,
      title: insight.title,
      summary: insight.summary,
      recommendedAction: insight.recommended_action,
      explanation: insight.explanation,
      explanationKey: insight.explanation_key,
      sourceSummary: insight.source_summary,
      sourceSummaryKey: insight.source_summary_key,
      target: insight.target,
      href: insight.href,
      sourceCount: insight.source_count,
      userState: insight.user_state ?? 'active',
      userStateUpdatedAt: insight.user_state_updated_at ?? null,
    })),
    dataQuality: {
      visibleSurfaceCount: response.data_quality.visible_surface_count,
      personalizedSignalCount: response.data_quality.personalized_signal_count,
      summary: response.data_quality.summary,
    },
    freshnessWarnings: response.freshness_warnings,
    freshnessWarningKeys: response.freshness_warning_keys,
  };
}

function mapGuestIntelligenceSnapshot(
  response: GuestExperienceBootstrapResponse['guest_intelligence_snapshots'][number],
): GuestIntelligenceSnapshot {
  return {
    id: response.id,
    occasionId: response.occasion_id,
    actorId: response.actor_id,
    generatedAt: response.generated_at,
    status: response.status,
    confidence: response.confidence,
    headline: response.headline,
    summary: response.summary,
    sourceKeys: response.source_keys.slice(),
    sourceHash: response.source_hash,
    payload: mapGuestIntelligence(response.payload) as GuestIntelligenceSummary,
    createdAt: response.created_at,
  };
}

function mapRecentCommunicationDelivery(
  response: GuestExperienceBootstrapResponse['recent_communications'][number]['delivery'],
) {
  return {
    id: response.id,
    status: response.status,
    recipientDisplayName: response.recipient_display_name,
    targetAddress: response.target_address,
    queuedAt: response.queued_at,
    deliveredAt: response.delivered_at,
  };
}

function mapRecentCommunicationMessage(
  response: GuestExperienceBootstrapResponse['recent_communications'][number]['message'],
) {
  if (!response) {
    return null;
  }

  return {
    id: response.id,
    subjectText: response.subject_text,
    bodyText: response.body_text,
    communicationEventKey: response.communication_event_key,
  };
}

function mapUnifiedTaskSummary(
  response: GuestExperienceBootstrapResponse['task_summary'],
): UnifiedTaskSummaryRecord {
  return {
    totalCount: response.total_count,
    pendingCount: response.pending_count,
    readyCount: response.ready_count,
    blockedCount: response.blocked_count,
    completedCount: response.completed_count,
    dismissedCount: response.dismissed_count,
    expiredCount: response.expired_count,
    invalidatedCount: response.invalidated_count,
    overdueCount: response.overdue_count,
    criticalCount: response.critical_count,
    importantCount: response.important_count,
    normalCount: response.normal_count,
    informationalCount: response.informational_count,
  };
}

function mapUnifiedTask(
  response: GuestExperienceBootstrapResponse['unified_tasks'][number],
): UnifiedTaskRecord {
  return {
    id: response.id,
    taskKey: response.task_key,
    definitionKey: response.definition_key,
    kind: response.kind,
    audience: response.audience,
    sourceDomain: response.source_domain,
    owner: {
      ownerType: response.owner.owner_type,
      ownerId: response.owner.owner_id,
      actorId: response.owner.actor_id,
      principalType: response.owner.principal_type,
    },
    context: {
      occasionId: response.context.occasion_id,
      eventId: response.context.event_id,
      moduleKey: response.context.module_key,
      resourceType: response.context.resource_type,
      resourceId: response.context.resource_id,
    },
    priority: response.priority,
    status: response.status,
    completionMode: response.completion_mode,
    title: response.title,
    summary: response.summary,
    dueAt: response.due_at,
    visibleFrom: response.visible_from,
    expiresAt: response.expires_at,
    actionableBy: response.actionable_by,
    blockers: response.blockers.map((blocker) => ({
      code: blocker.code,
      summary: blocker.summary,
      blockingTaskId: blocker.blocking_task_id,
    })),
    actions: response.actions.map((action) => ({
      kind: action.kind,
      label: action.label,
      href: action.href,
    })),
    payload: response.payload,
    completedAt: response.completed_at,
    dismissedAt: response.dismissed_at,
    invalidatedAt: response.invalidated_at,
    createdAt: response.created_at,
    updatedAt: response.updated_at,
  };
}

function mapGuestIdentityContext(
  response:
    | GuestOccasionContextResponse['identity_summary']
    | GuestRsvpResponse['identity_context'],
): GuestIdentityContext {
  return {
    accessActorId:
      'access_actor_id' in response ? response.access_actor_id : null,
    responseUnitActorId: response.response_unit_actor_id,
    responseUnitDisplayName: response.response_unit_display_name,
    responseScope: response.response_scope,
    participantActorIds:
      'participant_actor_ids' in response ? response.participant_actor_ids : [],
    participantDisplayNames: response.participant_display_names,
    delegateActorIds:
      'delegate_actor_ids' in response ? response.delegate_actor_ids : [],
    delegateDisplayNames: response.delegate_display_names,
    householdLabels: response.household_labels,
    relationshipLabels: response.relationship_labels,
    summary: response.summary,
  };
}

function mapAssetRecord(
  response: OccasionAssetsResponse['items'][number]['asset'],
): AssetRecord {
  return {
    id: response.id,
    occasionId: response.occasion_id,
    uploadedByUserId: response.uploaded_by_user_id,
    uploadedByActorId: response.uploaded_by_actor_id,
    assetKind: response.asset_kind,
    status: response.status,
    storageProviderKey: response.storage_provider_key,
    storageObjectKey: response.storage_object_key,
    fileName: response.file_name,
    mimeType: response.mime_type,
    fileSizeBytes: response.file_size_bytes,
    checksumSha256: response.checksum_sha256,
    metadata: response.metadata,
    createdAt: response.created_at,
    updatedAt: response.updated_at,
    processingStartedAt: response.processing_started_at,
    processedAt: response.processed_at,
    failureReason: response.failure_reason,
    archivedAt: response.archived_at,
  };
}

function mapAssetVariant(
  response: OccasionAssetsResponse['items'][number]['variants'][number],
): AssetVariantRecord {
  return {
    id: response.id,
    assetId: response.asset_id,
    variantKey: response.variant_key,
    status: response.status,
    mimeType: response.mime_type,
    storageObjectKey: response.storage_object_key,
    width: response.width,
    height: response.height,
    metadata: response.metadata,
    createdAt: response.created_at,
    updatedAt: response.updated_at,
  };
}

function mapAssetAttachment(
  response: OccasionAssetsResponse['items'][number]['attachments'][number],
): AssetAttachmentRecord {
  return {
    id: response.id,
    assetId: response.asset_id,
    occasionId: response.occasion_id,
    eventId: response.event_id,
    moduleInstallationId: response.module_installation_id,
    resourceType: response.resource_type,
    resourceId: response.resource_id,
    attachmentRole: response.attachment_role,
    createdAt: response.created_at,
  };
}

function mapAssetVisibilityRule(
  response: OccasionAssetsResponse['items'][number]['visibility_rules'][number],
): AssetVisibilityRuleRecord {
  return {
    id: response.id,
    assetId: response.asset_id,
    visibilityScope: response.visibility_scope,
    principalType: response.principal_type,
    subjectId: response.subject_id,
    isAllowed: response.is_allowed,
    createdAt: response.created_at,
  };
}

function mapAssetDetail(
  response: OccasionAssetsResponse['items'][number],
): AssetDetail {
  return {
    asset: mapAssetRecord(response.asset),
    variants: response.variants.map(mapAssetVariant),
    attachments: response.attachments.map(mapAssetAttachment),
    visibilityRules: response.visibility_rules.map(mapAssetVisibilityRule),
    accessUrl: response.access_url,
  };
}

function mapGalleryItem(
  response: GuestGalleryResponse['items'][number]['gallery_item'],
): GalleryItemRecord {
  return {
    id: response.id,
    occasionId: response.occasion_id,
    eventId: response.event_id,
    installationId: response.installation_id,
    assetId: response.asset_id,
    uploaderKind: response.uploader_kind,
    uploaderActorId: response.uploader_actor_id,
    uploaderUserId: response.uploader_user_id,
    uploaderDisplayName: response.uploader_display_name,
    caption: response.caption,
    moderationStatus: response.moderation_status,
    moderationNote: response.moderation_note,
    moderatedByUserId: response.moderated_by_user_id,
    moderatedAt: response.moderated_at,
    createdAt: response.created_at,
    updatedAt: response.updated_at,
  };
}

function mapGalleryViewItem(
  response: GuestGalleryResponse['items'][number],
): GuestGalleryViewItem {
  return {
    galleryItem: mapGalleryItem(response.gallery_item),
    asset: mapAssetRecord(response.asset),
    assetUrl: response.asset_url,
    variants: response.variants.map(mapAssetVariant),
    displayUrl: response.display_url,
    downloadUrl: response.download_url,
    event: response.event ? mapEvent(response.event) : null,
    reactions: response.reactions ?? [],
    comments: response.comments ?? [],
  };
}

function mapGalleryAlbum(
  response: GuestGalleryResponse['albums'][number],
): GuestGalleryAlbum {
  return {
    album: mapGalleryAlbumRecord(response.album),
    isOpen: response.is_open ?? true,
    items: response.items.map((item) => ({
      albumItem: mapGalleryAlbumItemRecord(item.album_item),
      galleryItem: mapGalleryItem(item.gallery_item),
    })),
  };
}

function mapGalleryAlbumRecord(
  response: ApiGalleryAlbumRecord,
): GalleryAlbumRecord {
  return {
    id: response.id,
    occasionId: response.occasion_id,
    eventId: response.event_id,
    installationId: response.installation_id,
    albumKey: response.album_key,
    name: response.name,
    description: response.description,
    scope: response.scope,
    visibility: response.visibility,
    opensAt: response.opens_at ?? null,
    closedAt: response.closed_at ?? null,
    sortOrder: response.sort_order,
    createdByUserId: response.created_by_user_id,
    createdAt: response.created_at,
    updatedAt: response.updated_at,
  };
}

function mapGalleryAlbumItemRecord(
  response: ApiGalleryAlbumItemRecord,
): GalleryAlbumItemRecord {
  return {
    id: response.id,
    albumId: response.album_id,
    galleryItemId: response.gallery_item_id,
    sortOrder: response.sort_order,
    createdAt: response.created_at,
  };
}

function mapGalleryUploadSession(
  response: ApiGalleryUploadSessionRecord,
): GuestGalleryUploadSession {
  return {
    id: response.id,
    eventId: response.event_id,
    albumId: response.album_id,
    fileName: response.file_name,
    mimeType: response.mime_type,
    fileSizeBytes: response.file_size_bytes,
    caption: response.caption,
    signedUploadUrl: response.signed_upload_url,
    uploadHeaders: response.upload_headers,
    status: response.status,
    expiresAt: response.expires_at,
  };
}

function mapTimelineItem(
  response: GuestTimelineResponse['items'][number]['timeline_item'],
): TimelineItemRecord {
  return {
    id: response.id,
    occasionId: response.occasion_id,
    eventId: response.event_id,
    contextId: response.context_id ?? response.event_id,
    contextType: response.context_type ?? 'event',
    installationId: response.installation_id,
    itemKey: response.item_key,
    title: response.title,
    description: response.description,
    startsAt: response.starts_at,
    endsAt: response.ends_at,
    durationMinutes: response.duration_minutes,
    sortOrder: response.sort_order,
    status: response.status,
    visibility: response.visibility,
    visibilityScope: response.visibility_scope ?? response.visibility,
    ownerActorId: response.owner_actor_id,
    locationLabel: response.location_label,
    notes: response.notes,
    createdByUserId: response.created_by_user_id,
    updatedByUserId: response.updated_by_user_id,
    createdAt: response.created_at,
    updatedAt: response.updated_at,
  };
}

function mapTimelineViewItem(
  response: GuestTimelineResponse['items'][number],
): GuestTimelineViewItem {
  return {
    timelineItem: mapTimelineItem(response.timeline_item),
    event: mapEvent(response.event),
  };
}

function mapSeatingPlan(
  response: GuestSeatingResponse['items'][number]['seating_plan'],
): SeatingPlanRecord {
  return {
    id: response.id,
    occasionId: response.occasion_id,
    eventId: response.event_id,
    installationId: response.installation_id,
    planKey: response.plan_key,
    versionNumber: response.version_number,
    basedOnPlanId: response.based_on_plan_id,
    displayName: response.display_name,
    plannerNotes: response.planner_notes,
    status: response.status,
    publishedAt: response.published_at,
    publishedByUserId: response.published_by_user_id,
    createdByUserId: response.created_by_user_id,
    updatedByUserId: response.updated_by_user_id,
    createdAt: response.created_at,
    updatedAt: response.updated_at,
  };
}

function mapSeatingTable(
  response: GuestSeatingResponse['items'][number]['table'],
): SeatingTableRecord {
  return {
    id: response.id,
    occasionId: response.occasion_id,
    eventId: response.event_id,
    seatingPlanId: response.seating_plan_id,
    zoneId: response.zone_id,
    label: response.label,
    capacity: response.capacity,
    sortOrder: response.sort_order,
    shape: response.shape,
    notes: response.notes,
    layoutX: response.layout_x ?? null,
    layoutY: response.layout_y ?? null,
    layoutWidth: response.layout_width ?? null,
    layoutHeight: response.layout_height ?? null,
    layoutRotation: response.layout_rotation ?? null,
    createdAt: response.created_at,
    updatedAt: response.updated_at,
  };
}

function mapSeatingAssignment(
  response: GuestSeatingResponse['items'][number]['assignment'],
): SeatingAssignmentRecord {
  return {
    id: response.id,
    occasionId: response.occasion_id,
    eventId: response.event_id,
    seatingPlanId: response.seating_plan_id,
    seatingTableId: response.seating_table_id,
    actorId: response.actor_id,
    assignmentSource: response.assignment_source,
    isLocked: response.is_locked,
    seatNumber: response.seat_number,
    seatLabel: response.seat_label,
    notes: response.notes,
    createdAt: response.created_at,
    updatedAt: response.updated_at,
  };
}

function mapGuestSeatingItem(
  response: GuestSeatingResponse['items'][number],
): GuestSeatingViewItem {
  return {
    seatingPlan: mapSeatingPlan(response.seating_plan),
    table: mapSeatingTable(response.table),
    assignment: mapSeatingAssignment(response.assignment),
    event: mapEvent(response.event),
  };
}

function mapSharedPlanningBoard(
  response: GuestSharedPlanningResponse['boards'][number]['board'],
): SharedPlanningBoardRecord {
  return {
    id: response.id,
    occasionId: response.occasion_id,
    installationId: response.installation_id,
    moduleKey: response.module_key,
    boardKey: response.board_key,
    title: response.title,
    description: response.description,
    scope: response.scope,
    eventId: response.event_id,
    defaultItemType: response.default_item_type,
    visibility: response.visibility,
    claimPolicy: response.claim_policy,
    allowGuestNotes: response.allow_guest_notes,
    claimDeadlineAt: response.claim_deadline_at,
    status: response.status,
    targetActorIds: response.target_actor_ids,
    createdAt: response.created_at,
    updatedAt: response.updated_at,
    archivedAt: response.archived_at,
  };
}

function mapSharedPlanningItem(
  response: GuestSharedPlanningResponse['boards'][number]['items'][number]['item'],
): SharedPlanningItemRecord {
  return {
    id: response.id,
    boardId: response.board_id,
    occasionId: response.occasion_id,
    title: response.title,
    description: response.description,
    itemType: response.item_type,
    status: response.status,
    claimPolicy: response.claim_policy,
    quantityNeeded: response.quantity_needed,
    quantityUnit: response.quantity_unit,
    allowPartialClaims: response.allow_partial_claims,
    suggestedActorIds: response.suggested_actor_ids,
    sortOrder: response.sort_order,
    plannerNotes: response.planner_notes,
    completionMode: response.completion_mode ?? 'completable',
    completableFromAt: response.completable_from_at ?? null,
    completableUntilAt: response.completable_until_at ?? null,
    remindAfterComplete: response.remind_after_complete ?? false,
    createdAt: response.created_at,
    updatedAt: response.updated_at,
    archivedAt: response.archived_at,
  };
}

function mapSharedPlanningClaim(
  response: GuestSharedPlanningResponse['boards'][number]['items'][number]['my_claim'],
): SharedPlanningClaimRecord | null {
  if (!response) {
    return null;
  }

  return {
    id: response.id,
    boardId: response.board_id,
    itemId: response.item_id,
    occasionId: response.occasion_id,
    actorId: response.actor_id,
    claimedQuantity: response.claimed_quantity,
    claimStatus: response.claim_status,
    guestNote: response.guest_note,
    source: response.source,
    createdAt: response.created_at,
    updatedAt: response.updated_at,
    cancelledAt: response.cancelled_at,
  };
}

function mapGuestSharedPlanningView(
  response: GuestSharedPlanningResponse['boards'][number],
): GuestSharedPlanningViewItem {
  return {
    board: mapSharedPlanningBoard(response.board),
    event: response.event ? mapEvent(response.event) : null,
    items: response.items.map((item) => ({
      item: mapSharedPlanningItem(item.item),
      myClaim: mapSharedPlanningClaim(item.my_claim),
      progress: {
        claimantCount: item.progress.claimant_count,
        activeClaimCount: item.progress.active_claim_count,
        quantityNeeded: item.progress.quantity_needed,
        quantityClaimed: item.progress.quantity_claimed,
        quantityRemaining: item.progress.quantity_remaining,
        fulfilledClaimCount: item.progress.fulfilled_claim_count,
      },
    })),
  };
}

function mapFormSubmission(
  response: ApiFormSubmissionRecord,
): FormSubmissionRecord {
  return {
    id: response.id,
    formId: response.form_id,
    occasionId: response.occasion_id,
    eventId: response.event_id,
    formVersionId: response.form_version_id ?? null,
    formVersionNumber: response.form_version_number ?? null,
    revisionNumber: response.revision_number ?? 1,
    submissionScope: response.submission_scope,
    submissionSubjectActorId: response.submission_subject_actor_id,
    submittedByActorId: response.submitted_by_actor_id,
    invitationUnitActorId: response.invitation_unit_actor_id,
    guestDisplayName: response.guest_display_name,
    accessMode: response.access_mode,
    status: response.status,
    correctionReason: response.correction_reason ?? null,
    correctionRequestedAt: response.correction_requested_at ?? null,
    correctionRequestedByUserId:
      response.correction_requested_by_user_id ?? null,
    answers: response.answers.map((answer) => ({
      fieldId: answer.field_id,
      fieldKey: answer.field_key,
      value: answer.value,
    })),
    completion: response.completion
      ? {
          visibleFieldCount: response.completion.visible_field_count,
          requiredFieldCount: response.completion.required_field_count,
          answeredRequiredFieldCount:
            response.completion.answered_required_field_count,
          completionPercent: response.completion.completion_percent,
          complete: response.completion.complete,
          pendingFieldIds: response.completion.pending_field_ids,
        }
      : undefined,
    revisions: response.revisions?.map((revision) => ({
      id: revision.id,
      submissionId: response.id,
      revisionNumber: revision.revision_number,
      formVersionId: revision.form_version_id,
      formVersionNumber: revision.form_version_number,
      submittedByActorId: revision.submitted_by_actor_id,
      answers: revision.answers.map((answer) => ({
        fieldId: answer.field_id,
        fieldKey: answer.field_key,
        value: answer.value,
      })),
      completion: revision.completion
        ? {
            visibleFieldCount: revision.completion.visible_field_count,
            requiredFieldCount: revision.completion.required_field_count,
            answeredRequiredFieldCount:
              revision.completion.answered_required_field_count,
            completionPercent: revision.completion.completion_percent,
            complete: revision.completion.complete,
            pendingFieldIds: revision.completion.pending_field_ids,
          }
        : null,
      submittedAt: revision.submitted_at,
    })),
    submittedAt: response.submitted_at,
    updatedAt: response.updated_at,
  };
}

function mapVisibleForm(response: ApiGuestVisibleForm): GuestVisibleFormRecord {
  const mapConditionGroup = (
    condition:
      | NonNullable<
          ApiGuestVisibleForm['form']['fields'][number]['visibility_condition']
        >
      | null
      | undefined,
  ) =>
    condition
      ? {
          mode: condition.mode,
          rules: condition.rules.map((rule) => ({
            sourceFieldId: rule.source_field_id,
            operator: rule.operator,
            value: rule.value,
          })),
        }
      : null;
  const mapField = (field: ApiGuestVisibleForm['form']['fields'][number]) => ({
    id: field.id,
    formId: field.form_id,
    sectionId: field.section_id ?? null,
    fieldKey: field.field_key,
    label: field.label,
    helpText: field.help_text,
    fieldType: field.field_type,
    required: field.required,
    sortOrder: field.sort_order,
    placeholder: field.placeholder,
    options: field.options.map((option) => ({
      value: option.value,
      label: option.label,
    })),
    validation: {
      minLength: field.validation.min_length ?? null,
      maxLength: field.validation.max_length ?? null,
      minValue: field.validation.min_value ?? null,
      maxValue: field.validation.max_value ?? null,
      pattern: field.validation.pattern ?? null,
      minSelections: field.validation.min_selections ?? null,
      maxSelections: field.validation.max_selections ?? null,
    },
    defaultValue: field.default_value ?? null,
    visibilityCondition: mapConditionGroup(field.visibility_condition),
    requiredCondition: mapConditionGroup(field.required_condition),
    content: field.content ?? null,
    localizationKey: field.localization_key ?? null,
  });
  const mapSection = (
    section: NonNullable<ApiGuestVisibleForm['form']['sections']>[number],
  ) => ({
    id: section.id,
    formId: section.form_id,
    sectionKey: section.section_key,
    title: section.title,
    description: section.description,
    sortOrder: section.sort_order,
    visibilityCondition: mapConditionGroup(section.visibility_condition),
  });
  const mapVersion = (
    version: NonNullable<ApiGuestVisibleForm['form']['versions']>[number],
  ) => ({
    id: version.id,
    formId: version.form_id,
    versionNumber: version.version_number,
    status: version.status,
    schemaFingerprint: version.schema_fingerprint,
    sections: version.sections.map(mapSection),
    fields: version.fields.map(mapField),
    createdAt: version.created_at,
    publishedAt: version.published_at,
    basedOnVersionId: version.based_on_version_id,
  });

  return {
    form: {
      id: response.form.id,
      occasionId: response.form.occasion_id,
      installationId: response.form.installation_id,
      moduleKey: response.form.module_key,
      formKey: response.form.form_key,
      displayName: response.form.display_name,
      description: response.form.description,
      audience: response.form.audience,
      status: response.form.status,
      scope: response.form.scope,
      eventIds: response.form.event_ids,
      formSetKey: response.form.form_set_key,
      targetActorIds: response.form.target_actor_ids,
      submissionScope: response.form.submission_scope,
      sections: response.form.sections?.map(mapSection) ?? [],
      fields: response.form.fields.map(mapField),
      currentVersionId: response.form.current_version_id ?? null,
      currentVersionNumber: response.form.current_version_number ?? 1,
      currentVersion: response.form.current_version
        ? mapVersion(response.form.current_version)
        : undefined,
      versions: response.form.versions?.map(mapVersion) ?? [],
      createdAt: response.form.created_at,
      updatedAt: response.form.updated_at,
      archivedAt: response.form.archived_at,
    },
    events: response.events.map(mapEvent).map((event) => ({
      id: event.id,
      eventKey: event.eventKey,
      displayName: event.displayName,
    })),
    submission: response.submission
      ? mapFormSubmission(response.submission)
      : null,
    submissionLocked: response.submission_locked,
  };
}

function mapRsvpInvitation(
  response: ApiRsvpInvitationRecord,
): RsvpInvitationRecord {
  const audienceRuleType =
    response.audience_rule_type ?? response.audience_type ?? 'manual_selection';

  return {
    id: response.id,
    occasionId: response.occasion_id,
    invitationKey: response.invitation_key,
    primaryActorId: response.primary_actor_id,
    invitationUnitActorId: response.invitation_unit_actor_id,
    responseScope: response.response_scope,
    invitedEventIds: response.invited_event_ids,
    publishingAssetId: response.publishing_asset_id,
    deliveryModeDefault: response.delivery_mode_default ?? 'automatic',
    allowAddOthers: response.allow_add_others ?? false,
    audienceType: response.audience_type ?? 'manual_selection',
    audienceRuleType: isRsvpInvitationAudienceRuleType(audienceRuleType)
      ? audienceRuleType
      : 'manual_selection',
    audienceRuleConfig: structuredClone(response.audience_rule_config ?? {}),
    genericInvitationToken: response.generic_invitation_token ?? null,
    genericJoinGroupId: response.generic_join_group_id ?? null,
    language: response.language ?? null,
    messageBodyTemplate: response.message_body_template ?? null,
    openingExperience: normalizeInvitationOpeningExperience(
      response.opening_experience
        ? {
            type: response.opening_experience.type,
            configuration: response.opening_experience.configuration,
            showOnFirstVisitOnly:
              response.opening_experience.show_on_first_visit_only,
            allowSkip: response.opening_experience.allow_skip,
          }
        : null,
    ),
    status: response.status,
    guestDisplayName: response.guest_display_name,
    createdAt: response.created_at,
    updatedAt: response.updated_at,
  };
}

function mapGuestPublishableInvitation(
  response: ApiGuestPublishableInvitation | null,
): GuestPublishableInvitation | null {
  return response
    ? {
        id: response.id,
        name: response.name,
        publishingType: response.publishing_type,
        width: response.width,
        height: response.height,
        document: response.document,
        imageUrlByAssetId: response.image_url_by_asset_id,
      }
    : null;
}

function mapRsvpResponse(response: ApiRsvpResponseRecord): RsvpResponseRecord {
  return {
    id: response.id,
    occasionId: response.occasion_id,
    invitationId: response.invitation_id,
    eventId: response.event_id,
    responseScope: response.response_scope,
    responseUnitActorId: response.response_unit_actor_id,
    participantActorIds: response.participant_actor_ids,
    status: response.status,
    respondedByActorId: response.responded_by_actor_id,
    source: response.source,
    note: response.note,
    linkedFormId: response.linked_form_id,
    linkedFormSubmissionId: response.linked_form_submission_id,
    createdAt: response.created_at,
    updatedAt: response.updated_at,
  };
}

export interface GuestSearchDocument {
  document_id: string;
  resource_id: string;
  resource_type: string;
  title: string;
  subtitle: string | null;
  metadata: Record<string, unknown>;
}

export interface GuestSearchResponse {
  items: Array<{
    document: GuestSearchDocument;
    score: number | null;
    highlights: string[];
    snippets: Array<{
      field: string;
      text: string;
      highlightedText: string;
    }>;
  }>;
  groups: Array<{
    resourceType: string;
    label: string;
    icon: string;
    total: number;
    viewAllPath: string | null;
    items: GuestSearchResponse['items'];
  }>;
  total: number;
  analyticsId: string | null;
  metadata: {
    latencyMs: number;
    partial: boolean;
    partialFailures: Array<{
      resourceType: string;
      reason: string;
    }>;
    surface: GuestSearchSurface | null;
  };
}

export function createGuestApi(httpClient: HttpClient) {
  async function getRegistryAfterMutation(
    occasionSlug: string,
    guestToken: string,
    path: string,
    body?: Record<string, unknown>,
  ) {
    const response = await httpClient.request<GuestRegistryResponse>({
      method: 'POST',
      path,
      query: {
        guest_token: guestToken,
      },
      body,
    });

    return {
      occasionId: response.occasion_id,
      registry: mapRegistry(response.registry),
      items: response.items.map(mapGuestRegistryViewItem),
      filters: {
        availableCount: response.filters.available_count,
        claimedCount: response.filters.claimed_count,
      },
    };
  }

  return {
    async exchangeToken(distributionToken: string): Promise<{
      sessionToken: string;
      occasionId: string;
      actorId: string | null;
      accessMode: 'guest_token' | 'claimed_guest';
      expiresAt: string | null;
    }> {
      const response = await httpClient.request<{
        session_token: string;
        occasion_id: string;
        actor_id: string | null;
        access_mode: 'guest_token' | 'claimed_guest';
        expires_at: string | null;
      }>({
        method: 'POST',
        path: '/v1/guest/sessions/exchange',
        body: { guest_token: distributionToken },
      });
      return {
        sessionToken: response.session_token,
        occasionId: response.occasion_id,
        actorId: response.actor_id,
        accessMode: response.access_mode,
        expiresAt: response.expires_at,
      };
    },
    async getAiTaskCatalog(occasionSlug: string, guestToken: string) {
      return httpClient.request<AiTaskCatalogResponse>({
        method: 'GET',
        path: `/v1/guest/occasions/${occasionSlug}/ai/tasks`,
        query: {
          guest_token: guestToken,
        },
      });
    },
    async executeAiTask(
      occasionSlug: string,
      guestToken: string,
      taskKey: 'guest_faq_answer',
      input: AiTaskExecutionRequest,
    ) {
      return httpClient.request<AiTaskExecutionResponse>({
        method: 'POST',
        path: `/v1/guest/occasions/${occasionSlug}/ai/tasks/${taskKey}`,
        query: {
          guest_token: guestToken,
        },
        body: input,
      });
    },
    async getExperienceBootstrap(
      occasionSlug: string,
      guestToken: string,
    ): Promise<GuestExperienceBootstrap> {
      const response =
        await httpClient.request<GuestExperienceBootstrapResponse>({
          method: 'GET',
          path: `/v1/guest/occasions/${occasionSlug}/bootstrap`,
          query: {
            guest_token: guestToken,
          },
          offlineCache: guestOfflineCache(
            occasionSlug,
            guestToken,
            'experience-bootstrap',
          ),
        });

      const result = {
        context: {
          occasion: mapOccasion(response.occasion),
          guestDisplayName: response.guest_display_name,
          accessMode: response.access_mode,
          identitySummary: mapGuestIdentityContext(response.identity_summary),
          unifiedContext: mapUnifiedContext(response.unified_context),
          multiLanguageCapability: {
            key: response.multi_language_capability.key,
            enabled: response.multi_language_capability.enabled,
            source: response.multi_language_capability.source,
            sourceKey: response.multi_language_capability.source_key,
            reason: response.multi_language_capability.reason,
          },
          localizationContext: mapLocalizationContext(
            response.localization_context,
          ),
        },
        openingExperience: response.opening_experience
          ? normalizeInvitationOpeningExperience({
              type: response.opening_experience.type,
              configuration: response.opening_experience.configuration,
              showOnFirstVisitOnly:
                response.opening_experience.show_on_first_visit_only,
              allowSkip: response.opening_experience.allow_skip,
            })
          : null,
        designTheme: mapGuestTheme(response.design_theme),
        eventVisualOverrides: response.event_visual_overrides,
        tokenInheritance: response.token_inheritance,
        guestWebsite: response.guest_website,
        events: response.events.map(mapEvent),
        forms: response.forms.map(mapVisibleForm),
        recentCommunications: response.recent_communications.map((item) => ({
          delivery: mapRecentCommunicationDelivery(item.delivery),
          message: mapRecentCommunicationMessage(item.message),
          event: item.event ? mapEvent(item.event) : null,
          deepLink: item.deep_link,
        })),
        journeySummary: mapJourneySummary(response.journey_summary),
        guestSession: mapGuestSession(response.guest_session),
        onboardingSummary: mapGuestOnboardingSummary(
          response.onboarding_summary,
        ),
        accessLifecycle: mapGuestAccessLifecycle(response.access_lifecycle),
        guestStateMachine: mapGuestLifecycleStateMachine(
          response.guest_state_machine,
        ),
        guestIntelligence: mapGuestIntelligence(response.guest_intelligence),
        guestIntelligenceSnapshots: (
          response.guest_intelligence_snapshots ?? []
        ).map(mapGuestIntelligenceSnapshot),
        taskSummary: mapUnifiedTaskSummary(response.task_summary),
        unifiedTasks: response.unified_tasks.map(mapUnifiedTask),
        tasks: response.tasks.map(mapGuestTask),
      };

      return copyOfflineCacheResponseMeta(response, result);
    },
    async updateGuestIntelligenceInsightState(
      occasionSlug: string,
      guestToken: string,
      insightKey: string,
      state: 'active' | 'acknowledged' | 'dismissed',
    ) {
      return httpClient.request<{
        occasion_id: string;
        scope: 'guest';
        actor_id: string | null;
        insight_key: string;
        state: 'active' | 'acknowledged' | 'dismissed';
        updated_at: string;
      }>({
        method: 'PATCH',
        path: `/v1/guest/occasions/${occasionSlug}/intelligence/insights/${encodeURIComponent(
          insightKey,
        )}/state`,
        query: {
          guest_token: guestToken,
        },
        body: {
          state,
        },
      });
    },
    async searchOccasionResources(
      occasionSlug: string,
      guestToken: string,
      filters: {
        query: string;
        resourceTypes?: string[];
        limit?: number;
        offset?: number;
        includeAnalytics?: boolean;
      },
    ): Promise<GuestSearchResponse> {
      const query = new URLSearchParams({
        guest_token: guestToken,
        query: filters.query,
      });

      if (filters.resourceTypes && filters.resourceTypes.length > 0) {
        query.set('resource_types', filters.resourceTypes.join(','));
      }

      if (typeof filters.limit === 'number') {
        query.set('limit', String(filters.limit));
      }

      if (typeof filters.offset === 'number') {
        query.set('offset', String(filters.offset));
      }

      if (filters.includeAnalytics) {
        query.set('include_analytics', 'true');
      }

      const response = await httpClient.request<GuestSearchResponseBody>({
        method: 'GET',
        path: `/v1/guest/occasions/${occasionSlug}/search?${query.toString()}`,
      });

      return mapGuestSearchResponse(response);
    },
    async getOccasionContext(
      occasionSlug: string,
      guestToken: string,
    ): Promise<GuestOccasionContext> {
      const response = await httpClient.request<GuestOccasionContextResponse>({
        method: 'GET',
        path: `/v1/guest/occasions/${occasionSlug}/context`,
        query: {
          guest_token: guestToken,
        },
        offlineCache: guestOfflineCache(
          occasionSlug,
          guestToken,
          'occasion-context',
        ),
      });

      const result = {
        occasion: mapOccasion(response.occasion),
        guestDisplayName: response.guest_display_name,
        accessMode: response.access_mode,
        identitySummary: mapGuestIdentityContext(response.identity_summary),
        unifiedContext: mapUnifiedContext(response.unified_context),
        multiLanguageCapability: {
          key: response.multi_language_capability.key,
          enabled: response.multi_language_capability.enabled,
          source: response.multi_language_capability.source,
          sourceKey: response.multi_language_capability.source_key,
          reason: response.multi_language_capability.reason,
        },
        localizationContext: mapLocalizationContext(
          response.localization_context,
        ),
      };

      return copyOfflineCacheResponseMeta(response, result);
    },
    async listVisibleEvents(
      occasionSlug: string,
      guestToken: string,
    ): Promise<EventSummary[]> {
      const response = await httpClient.request<OccasionEventListResponse>({
        method: 'GET',
        path: `/v1/guest/occasions/${occasionSlug}/events`,
        query: {
          guest_token: guestToken,
        },
        offlineCache: guestOfflineCache(occasionSlug, guestToken, 'events'),
      });

      return copyOfflineCacheResponseMeta(
        response,
        response.items.map(mapEvent),
      );
    },
    async getCommunicationPreferences(
      occasionSlug: string,
      guestToken: string,
    ): Promise<GuestCommunicationPreferences> {
      const response = await httpClient.request<{
        occasion_id: string;
        actor_id: string | null;
        preferred_locale_code: string | null;
        notification_preferences: Array<{
          id: string;
          channel_type:
            | 'email'
            | 'sms'
            | 'whatsapp'
            | 'push'
            | 'in_app'
            | 'webhook';
          is_enabled: boolean;
          preferred_locale_code: string | null;
          updated_at: string;
        }>;
      }>({
        method: 'GET',
        path: `/v1/guest/occasions/${occasionSlug}/preferences`,
        query: {
          guest_token: guestToken,
        },
      });

      return {
        occasionId: response.occasion_id,
        actorId: response.actor_id,
        preferredLocaleCode: response.preferred_locale_code,
        notificationPreferences: response.notification_preferences.map(
          (preference) => ({
            id: preference.id,
            channelType: preference.channel_type,
            isEnabled: preference.is_enabled,
            preferredLocaleCode: preference.preferred_locale_code,
            updatedAt: preference.updated_at,
          }),
        ),
      };
    },
    async updateCommunicationPreferences(
      occasionSlug: string,
      guestToken: string,
      input: {
        preferred_locale_code: string | null;
        email_enabled: boolean;
        in_app_enabled: boolean;
      },
    ): Promise<GuestCommunicationPreferences> {
      const response = await httpClient.request<{
        occasion_id: string;
        actor_id: string | null;
        preferred_locale_code: string | null;
        notification_preferences: Array<{
          id: string;
          channel_type:
            | 'email'
            | 'sms'
            | 'whatsapp'
            | 'push'
            | 'in_app'
            | 'webhook';
          is_enabled: boolean;
          preferred_locale_code: string | null;
          updated_at: string;
        }>;
      }>({
        method: 'PUT',
        path: `/v1/guest/occasions/${occasionSlug}/preferences`,
        query: {
          guest_token: guestToken,
        },
        body: input,
      });

      return {
        occasionId: response.occasion_id,
        actorId: response.actor_id,
        preferredLocaleCode: response.preferred_locale_code,
        notificationPreferences: response.notification_preferences.map(
          (preference) => ({
            id: preference.id,
            channelType: preference.channel_type,
            isEnabled: preference.is_enabled,
            preferredLocaleCode: preference.preferred_locale_code,
            updatedAt: preference.updated_at,
          }),
        ),
      };
    },
    async getMessagingInbox(
      occasionSlug: string,
      guestToken: string,
    ): Promise<GuestMessagingInboxItem[]> {
      const response = await httpClient.request<
        GuestMessagingInboxItemResponse[]
      >({
        method: 'GET',
        path: `/v1/guest/occasions/${occasionSlug}/messaging/inbox`,
        query: {
          guest_token: guestToken,
        },
        offlineCache: guestOfflineCache(
          occasionSlug,
          guestToken,
          'messaging-inbox',
        ),
      });

      const result = response.map(mapGuestMessagingInboxItem);

      return copyOfflineCacheResponseMeta(response, result);
    },
    async getMessagingThreads(
      occasionSlug: string,
      guestToken: string,
    ): Promise<GuestMessagingInboxItem[]> {
      return this.getMessagingInbox(occasionSlug, guestToken);
    },
    async getMessagingThread(
      occasionSlug: string,
      guestToken: string,
      threadId: string,
    ): Promise<GuestMessagingThreadDetail> {
      const response =
        await httpClient.request<GuestMessagingThreadDetailResponse>({
          method: 'GET',
          path: `/v1/guest/occasions/${occasionSlug}/messaging/threads/${threadId}`,
          query: {
            guest_token: guestToken,
          },
        });

      return mapGuestMessagingThreadDetail(response);
    },
    async postMessagingMessage(
      occasionSlug: string,
      guestToken: string,
      threadId: string,
      bodyText: string,
    ): Promise<{ threadId: string; message: GuestMessagingMessage }> {
      const response = await httpClient.request<{
        thread_id: string;
        message: GuestMessagingMessageResponse;
      }>({
        method: 'POST',
        path: `/v1/guest/occasions/${occasionSlug}/messaging/threads/${threadId}/messages`,
        query: {
          guest_token: guestToken,
        },
        body: {
          body_text: bodyText,
        },
      });

      return {
        threadId: response.thread_id,
        message: mapGuestMessagingMessage(response.message),
      };
    },
    async postMessagingThreadRead(
      occasionSlug: string,
      guestToken: string,
      threadId: string,
      lastReadMessageId?: string | null,
    ): Promise<void> {
      await httpClient.request({
        method: 'POST',
        path: `/v1/guest/occasions/${occasionSlug}/messaging/threads/${threadId}/read`,
        query: {
          guest_token: guestToken,
        },
        body: {
          ...(lastReadMessageId !== undefined
            ? { last_read_message_id: lastReadMessageId }
            : {}),
        },
      });
    },
    async getMessagingUnreadCount(occasionSlug: string, guestToken: string) {
      return httpClient.request<{
        total_unread_count: number;
        threads: Array<{ thread_id: string; unread_count: number }>;
      }>({
        method: 'GET',
        path: `/v1/guest/occasions/${occasionSlug}/messaging/unread-count`,
        query: {
          guest_token: guestToken,
        },
      });
    },
    async getMessagingNotificationPreferences(
      occasionSlug: string,
      guestToken: string,
    ): Promise<GuestMessagingNotificationPreferences> {
      const response = await httpClient.request<{
        guest_email_enabled: boolean;
        guest_sms_enabled: boolean;
        guest_whatsapp_enabled: boolean;
        guest_push_enabled: boolean;
        in_app_messages_enabled: boolean;
        contact_methods: {
          email: string | null;
          sms: string | null;
          whatsapp: string | null;
          push_enabled: boolean;
        };
      }>({
        method: 'GET',
        path: `/v1/guest/occasions/${occasionSlug}/messaging/notification-preferences`,
        query: {
          guest_token: guestToken,
        },
      });

      return {
        guestEmailEnabled: response.guest_email_enabled,
        guestSmsEnabled: response.guest_sms_enabled,
        guestWhatsappEnabled: response.guest_whatsapp_enabled,
        guestPushEnabled: response.guest_push_enabled,
        inAppMessagesEnabled: response.in_app_messages_enabled,
        contactMethods: {
          email: response.contact_methods.email,
          sms: response.contact_methods.sms,
          whatsapp: response.contact_methods.whatsapp,
          pushEnabled: response.contact_methods.push_enabled,
        },
      };
    },
    async updateMessagingNotificationPreferences(
      occasionSlug: string,
      guestToken: string,
      input: {
        guest_email_enabled: boolean;
        guest_sms_enabled: boolean;
        guest_whatsapp_enabled: boolean;
        guest_push_enabled: boolean;
        in_app_messages_enabled: boolean;
        contact_methods?: {
          email?: string | null;
          sms?: string | null;
          whatsapp?: string | null;
        };
      },
    ): Promise<GuestMessagingNotificationPreferences> {
      const response = await httpClient.request<{
        guest_email_enabled: boolean;
        guest_sms_enabled: boolean;
        guest_whatsapp_enabled: boolean;
        guest_push_enabled: boolean;
        in_app_messages_enabled: boolean;
        contact_methods: {
          email: string | null;
          sms: string | null;
          whatsapp: string | null;
          push_enabled: boolean;
        };
      }>({
        method: 'PUT',
        path: `/v1/guest/occasions/${occasionSlug}/messaging/notification-preferences`,
        query: {
          guest_token: guestToken,
        },
        body: input,
      });

      return {
        guestEmailEnabled: response.guest_email_enabled,
        guestSmsEnabled: response.guest_sms_enabled,
        guestWhatsappEnabled: response.guest_whatsapp_enabled,
        guestPushEnabled: response.guest_push_enabled,
        inAppMessagesEnabled: response.in_app_messages_enabled,
        contactMethods: {
          email: response.contact_methods.email,
          sms: response.contact_methods.sms,
          whatsapp: response.contact_methods.whatsapp,
          pushEnabled: response.contact_methods.push_enabled,
        },
      };
    },
    async createMessagingRealtimeToken(
      occasionSlug: string,
      guestToken: string,
    ) {
      return httpClient.request<{
        token: string;
        expires_at: string;
        url: string;
        socket_ping_interval_ms: number;
        socket_ping_timeout_ms: number;
      }>({
        method: 'POST',
        path: `/v1/guest/occasions/${occasionSlug}/messaging/realtime-token`,
        query: {
          guest_token: guestToken,
        },
        body: {},
      });
    },
    async getDraftState(
      occasionSlug: string,
      guestToken: string,
    ): Promise<GuestDraftState> {
      const response = await httpClient.request<{
        occasion_id: string;
        actor_id: string | null;
        rsvp_draft: Record<string, unknown> | null;
        rsvp_draft_updated_at: string | null;
        form_drafts: Record<string, unknown> | null;
        form_drafts_updated_at: string | null;
        support_requests: Record<string, unknown> | null;
        support_requests_updated_at: string | null;
      }>({
        method: 'GET',
        path: `/v1/guest/occasions/${occasionSlug}/drafts`,
        query: {
          guest_token: guestToken,
        },
      });

      return {
        occasionId: response.occasion_id,
        actorId: response.actor_id,
        rsvpDraft: response.rsvp_draft,
        rsvpDraftUpdatedAt: response.rsvp_draft_updated_at,
        formDrafts: response.form_drafts,
        formDraftsUpdatedAt: response.form_drafts_updated_at,
        supportRequests: response.support_requests,
        supportRequestsUpdatedAt: response.support_requests_updated_at,
      };
    },
    async updateDraftState(
      occasionSlug: string,
      guestToken: string,
      input: {
        rsvpDraft?: Record<string, unknown> | null;
        formDrafts?: Record<string, unknown> | null;
        rsvp_draft?: Record<string, unknown> | null;
        form_drafts?: Record<string, unknown> | null;
        support_requests?: Record<string, unknown> | null;
      },
    ): Promise<GuestDraftState> {
      const response = await httpClient.request<{
        occasion_id: string;
        actor_id: string | null;
        rsvp_draft: Record<string, unknown> | null;
        rsvp_draft_updated_at: string | null;
        form_drafts: Record<string, unknown> | null;
        form_drafts_updated_at: string | null;
        support_requests: Record<string, unknown> | null;
        support_requests_updated_at: string | null;
      }>({
        method: 'PUT',
        path: `/v1/guest/occasions/${occasionSlug}/drafts`,
        query: {
          guest_token: guestToken,
        },
        body: {
          ...(input.rsvpDraft !== undefined
            ? { rsvp_draft: input.rsvpDraft }
            : {}),
          ...(input.formDrafts !== undefined
            ? { form_drafts: input.formDrafts }
            : {}),
          ...(input.rsvp_draft !== undefined
            ? { rsvp_draft: input.rsvp_draft }
            : {}),
          ...(input.form_drafts !== undefined
            ? { form_drafts: input.form_drafts }
            : {}),
          ...(input.support_requests !== undefined
            ? { support_requests: input.support_requests }
            : {}),
        },
      });

      return {
        occasionId: response.occasion_id,
        actorId: response.actor_id,
        rsvpDraft: response.rsvp_draft,
        rsvpDraftUpdatedAt: response.rsvp_draft_updated_at,
        formDrafts: response.form_drafts,
        formDraftsUpdatedAt: response.form_drafts_updated_at,
        supportRequests: response.support_requests,
        supportRequestsUpdatedAt: response.support_requests_updated_at,
      };
    },
    async getSupportInbox(
      occasionSlug: string,
      guestToken: string,
    ): Promise<GuestSupportInbox> {
      const response = await httpClient.request<{
        occasion_id: string;
        actor_id: string | null;
        items: GuestSupportRequestResponse[];
        summary: {
          total_count: number;
          open_count: number;
          resolved_count: number;
        };
      }>({
        method: 'GET',
        path: `/v1/guest/occasions/${occasionSlug}/support-requests`,
        query: {
          guest_token: guestToken,
        },
      });

      return {
        occasionId: response.occasion_id,
        actorId: response.actor_id,
        items: response.items.map(mapGuestSupportRequest),
        summary: {
          totalCount: response.summary.total_count,
          openCount: response.summary.open_count,
          resolvedCount: response.summary.resolved_count,
        },
      };
    },
    async createSupportRequest(
      occasionSlug: string,
      guestToken: string,
      input: {
        question: string;
        route: string;
        metadata?: Record<string, unknown>;
      },
    ): Promise<GuestSupportRequest> {
      const response = await httpClient.request<GuestSupportRequestResponse>({
        method: 'POST',
        path: `/v1/guest/occasions/${occasionSlug}/support-requests`,
        query: {
          guest_token: guestToken,
        },
        body: input,
      });

      return mapGuestSupportRequest(response);
    },
    async replyToSupportRequest(
      occasionSlug: string,
      guestToken: string,
      requestId: string,
      input: {
        bodyText: string;
      },
    ): Promise<GuestSupportRequest> {
      const response = await httpClient.request<GuestSupportRequestResponse>({
        method: 'POST',
        path: `/v1/guest/occasions/${occasionSlug}/support-requests/${requestId}/replies`,
        query: {
          guest_token: guestToken,
        },
        body: {
          body_text: input.bodyText,
        },
      });

      return mapGuestSupportRequest(response);
    },
    async getReviewerPreview(
      occasionSlug: string,
      input: {
        reviewToken: string;
        previewMode:
          | 'public_landing'
          | 'invited_guest'
          | 'rsvp_pending'
          | 'rsvp_complete'
          | 'event_day';
        viewport?: 'desktop' | 'mobile';
      },
    ): Promise<GuestReviewerPreview> {
      const response = await httpClient.request<{
        occasion_slug: string;
        occasion_display_name: string;
        preview_mode: GuestReviewerPreview['previewMode'];
        viewport: GuestReviewerPreview['viewport'];
        expires_at: string;
        website_identity: {
          site_title_override: string | null;
          subtitle_tagline: string | null;
        };
        sections: Array<{
          id: string;
          type: string;
          section_key: string;
          anchor_label: string | null;
          content_payload: Record<string, unknown>;
        }>;
        nav_items: Array<{
          id: string;
          label: string;
          target_section_id: string | null;
          target_path: string | null;
        }>;
      }>({
        method: 'GET',
        path: `/v1/guest/occasions/${occasionSlug}/reviewer-preview`,
        query: {
          review_token: input.reviewToken,
          preview_mode: input.previewMode,
          viewport: input.viewport ?? 'desktop',
        },
      });

      return {
        occasionSlug: response.occasion_slug,
        occasionDisplayName: response.occasion_display_name,
        previewMode: response.preview_mode,
        viewport: response.viewport,
        expiresAt: response.expires_at,
        websiteIdentity: {
          siteTitleOverride: response.website_identity.site_title_override,
          subtitleTagline: response.website_identity.subtitle_tagline,
        },
        sections: response.sections.map((section) => ({
          id: section.id,
          type: section.type,
          sectionKey: section.section_key,
          anchorLabel: section.anchor_label,
          contentPayload: section.content_payload,
        })),
        navItems: response.nav_items.map((item) => ({
          id: item.id,
          label: item.label,
          targetSectionId: item.target_section_id,
          targetPath: item.target_path,
        })),
      };
    },
    async createSecureResumeCode(
      occasionSlug: string,
      guestToken: string,
      input: {
        resume_path?: string | null;
      },
    ): Promise<GuestSecureResumePacket> {
      const response = await httpClient.request<{
        occasion_slug: string;
        actor_id: string;
        resume_code: string;
        resume_path: string | null;
        created_at: string;
        expires_at: string;
      }>({
        method: 'POST',
        path: `/v1/guest/occasions/${occasionSlug}/resume-codes`,
        query: {
          guest_token: guestToken,
        },
        body: {
          resume_path: input.resume_path ?? null,
        },
      });

      return {
        occasionSlug: response.occasion_slug,
        actorId: response.actor_id,
        resumeCode: response.resume_code,
        resumePath: response.resume_path,
        createdAt: response.created_at,
        expiresAt: response.expires_at,
      };
    },
    async exchangeSecureResumeCode(
      occasionSlug: string,
      resumeCode: string,
    ): Promise<{
      sessionToken: string;
      occasionId: string;
      actorId: string;
      accessMode: 'guest_token' | 'claimed_guest';
      expiresAt: string;
      resumePath: string | null;
    }> {
      const response = await httpClient.request<{
        session_token: string;
        occasion_id: string;
        actor_id: string;
        access_mode: 'guest_token' | 'claimed_guest';
        expires_at: string;
        resume_path: string | null;
      }>({
        method: 'POST',
        path: `/v1/guest/occasions/${occasionSlug}/resume-codes/exchange`,
        body: {
          resume_code: resumeCode,
        },
      });

      return {
        sessionToken: response.session_token,
        occasionId: response.occasion_id,
        actorId: response.actor_id,
        accessMode: response.access_mode,
        expiresAt: response.expires_at,
        resumePath: response.resume_path,
      };
    },
    async listVisibleLocations(
      occasionSlug: string,
      guestToken: string,
      filters?: {
        type?: GuestVisibleLocation['type'];
        parentLocationId?: string;
        eventId?: string;
        visibilityLevel?:
          | 'public'
          | 'occasion_guest'
          | 'event_invited_guest'
          | 'rsvp_accepted_guest'
          | 'actor_group_specific'
          | 'vendor_role_specific'
          | 'planner_internal_only'
          | 'hidden';
        hasCoordinates?: boolean;
        hasVenueMap?: boolean;
        archived?: boolean;
      },
    ): Promise<{
      occasionId: string;
      locations: GuestVisibleLocation[];
      eventLinks: GuestVisibleLocationsResponse['event_links'];
      locationLinks: GuestVisibleLocationsResponse['location_links'];
      contacts: GuestVisibleLocationsResponse['contacts'];
      assets: GuestVisibleLocationsResponse['assets'];
      overlays: GuestVisibleLocationsResponse['overlays'];
      mapMarkers: GuestVisibleLocationsResponse['map_markers'];
    }> {
      const response = await httpClient.request<GuestVisibleLocationsResponse>({
        method: 'GET',
        path: `/v1/guest/occasions/${occasionSlug}/locations`,
        query: {
          guest_token: guestToken,
          ...(filters?.type ? { type: filters.type } : {}),
          ...(filters?.parentLocationId
            ? { parent_location_id: filters.parentLocationId }
            : {}),
          ...(filters?.eventId ? { event_id: filters.eventId } : {}),
          ...(filters?.visibilityLevel
            ? { visibility_level: filters.visibilityLevel }
            : {}),
          ...(filters?.hasCoordinates !== undefined
            ? { has_coordinates: filters.hasCoordinates }
            : {}),
          ...(filters?.hasVenueMap !== undefined
            ? { has_venue_map: filters.hasVenueMap }
            : {}),
          ...(filters?.archived !== undefined
            ? { archived: filters.archived }
            : {}),
        },
        offlineCache: guestOfflineCache(
          occasionSlug,
          guestToken,
          `locations:${JSON.stringify(filters ?? {})}`,
        ),
      });

      const result = {
        occasionId: response.occasion_id,
        locations: response.locations.map(mapGuestVisibleLocation),
        eventLinks: response.event_links,
        locationLinks: response.location_links,
        contacts: response.contacts,
        assets: response.assets,
        overlays: response.overlays,
        mapMarkers: response.map_markers,
      };

      return copyOfflineCacheResponseMeta(response, result);
    },
    async getVisibleLocationsMap(
      occasionSlug: string,
      guestToken: string,
    ): Promise<{
      occasion_id: string;
      markers: Array<{
        location_id: string;
        title: string;
        type: string;
        latitude: number;
        longitude: number;
        marker_icon_hint: string | null;
        marker_color_hint: string | null;
        event_ids: string[];
      }>;
    }> {
      return httpClient.request({
        method: 'GET',
        path: `/v1/guest/occasions/${occasionSlug}/locations/map`,
        query: {
          guest_token: guestToken,
        },
      });
    },
    async getVisibleVenueMap(
      occasionSlug: string,
      guestToken: string,
      locationId: string,
    ): Promise<{
      occasion_id: string;
      location: GuestVisibleLocationsResponse['locations'][number];
      overlays: GuestVisibleLocationsResponse['overlays'];
    }> {
      return httpClient.request({
        method: 'GET',
        path: `/v1/guest/occasions/${occasionSlug}/locations/${locationId}/venue-map`,
        query: {
          guest_token: guestToken,
        },
      });
    },
    async listTimelineSummary(
      occasionSlug: string,
      guestToken: string,
    ): Promise<GuestTimelineViewItem[]> {
      const response = await httpClient.request<GuestTimelineResponse>({
        method: 'GET',
        path: `/v1/guest/occasions/${occasionSlug}/timeline`,
        query: {
          guest_token: guestToken,
        },
        offlineCache: guestOfflineCache(occasionSlug, guestToken, 'timeline'),
      });

      return copyOfflineCacheResponseMeta(
        response,
        response.items.map(mapTimelineViewItem),
      );
    },
    async listSeatingSummary(
      occasionSlug: string,
      guestToken: string,
    ): Promise<GuestSeatingViewItem[]> {
      const response = await httpClient.request<GuestSeatingResponse>({
        method: 'GET',
        path: `/v1/guest/occasions/${occasionSlug}/seating`,
        query: {
          guest_token: guestToken,
        },
      });

      return response.items.map(mapGuestSeatingItem);
    },
    async listSharedPlanning(
      occasionSlug: string,
      guestToken: string,
    ): Promise<GuestSharedPlanningViewItem[]> {
      const response = await httpClient.request<GuestSharedPlanningResponse>({
        method: 'GET',
        path: `/v1/guest/occasions/${occasionSlug}/shared-planning`,
        query: {
          guest_token: guestToken,
        },
      });

      return response.boards.map(mapGuestSharedPlanningView);
    },
    async getRegistry(
      occasionSlug: string,
      guestToken: string,
    ): Promise<{
      occasionId: string;
      registry: RegistryRecord;
      items: GuestRegistryViewItem[];
      filters: {
        availableCount: number;
        claimedCount: number;
      };
    }> {
      const response = await httpClient.request<GuestRegistryResponse>({
        method: 'GET',
        path: `/v1/guest/occasions/${occasionSlug}/registry`,
        query: {
          guest_token: guestToken,
        },
      });

      return {
        occasionId: response.occasion_id,
        registry: mapRegistry(response.registry),
        items: response.items.map(mapGuestRegistryViewItem),
        filters: {
          availableCount: response.filters.available_count,
          claimedCount: response.filters.claimed_count,
        },
      };
    },
    async claimRegistryItem(
      occasionSlug: string,
      guestToken: string,
      itemId: string,
      input: {
        quantity: number;
        confirmationNote: string | null;
      },
    ) {
      return getRegistryAfterMutation(
        occasionSlug,
        guestToken,
        `/v1/guest/occasions/${occasionSlug}/registry/items/${itemId}/claim`,
        {
          quantity: input.quantity,
          confirmation_note: input.confirmationNote,
        },
      );
    },
    async unclaimRegistryItem(
      occasionSlug: string,
      guestToken: string,
      itemId: string,
    ) {
      return getRegistryAfterMutation(
        occasionSlug,
        guestToken,
        `/v1/guest/occasions/${occasionSlug}/registry/items/${itemId}/unclaim`,
      );
    },
    async claimSharedPlanningItem(
      occasionSlug: string,
      guestToken: string,
      itemId: string,
      input: {
        claimedQuantity: number | null;
        claimStatus: 'committed' | 'fulfilled' | 'cancelled';
        guestNote: string | null;
      },
    ): Promise<GuestSharedPlanningViewItem[]> {
      const response = await httpClient.request<GuestSharedPlanningResponse>({
        method: 'POST',
        path: `/v1/guest/occasions/${occasionSlug}/shared-planning/items/${itemId}/claim`,
        query: {
          guest_token: guestToken,
        },
        body: {
          claimed_quantity: input.claimedQuantity,
          claim_status: input.claimStatus,
          guest_note: input.guestNote,
        },
      });

      return response.boards.map(mapGuestSharedPlanningView);
    },
    async listVisibleForms(
      occasionSlug: string,
      guestToken: string,
    ): Promise<GuestVisibleFormRecord[]> {
      const response = await httpClient.request<GuestVisibleFormsResponse>({
        method: 'GET',
        path: `/v1/guest/occasions/${occasionSlug}/forms`,
        query: {
          guest_token: guestToken,
        },
      });

      return response.items.map(mapVisibleForm);
    },
    async listVisibleAssets(
      occasionSlug: string,
      guestToken: string,
    ): Promise<AssetDetail[]> {
      const response = await httpClient.request<OccasionAssetsResponse>({
        method: 'GET',
        path: `/v1/guest/occasions/${occasionSlug}/assets`,
        query: {
          guest_token: guestToken,
        },
      });

      return response.items.map(mapAssetDetail);
    },
    async getGallery(
      occasionSlug: string,
      guestToken: string,
      input?: {
        cursor?: string | null;
        limit?: number | null;
      },
    ): Promise<{
      occasionId: string;
      installationId: string;
      uploadAllowed: boolean;
      uploadPolicy: 'always_approve' | 'always_review' | 'blocked' | 'default';
      requiresReview: boolean;
      uploadOptions: Array<{
        eventId: string;
        albumId: string | null;
        displayName: string;
      }>;
      items: GuestGalleryViewItem[];
      myUploads: GuestGalleryViewItem[];
      albums: GuestGalleryAlbum[];
      uploadConstraints: {
        maxFileSizeBytes: number;
        acceptedMimePrefixes: string[];
      };
      pageInfo: {
        limit: number;
        nextCursor: string | null;
      };
      favoriteItemIds: string[];
      pendingUploadSessions: GuestGalleryUploadSession[];
    }> {
      const response = await httpClient.request<GuestGalleryResponse>({
        method: 'GET',
        path: `/v1/guest/occasions/${occasionSlug}/gallery`,
        query: {
          ...galleryGuestTokenQuery(guestToken),
          ...(input?.cursor ? { cursor: input.cursor } : {}),
          ...(input?.limit ? { limit: input.limit } : {}),
        },
      });

      return {
        occasionId: response.occasion_id,
        installationId: response.installation_id,
        uploadAllowed: response.upload_allowed,
        uploadPolicy: response.upload_policy ?? 'default',
        requiresReview: response.requires_review ?? true,
        uploadOptions: response.upload_options.map((option) => ({
          eventId: option.event_id,
          albumId: option.album_id,
          displayName: option.display_name,
        })),
        items: response.items.map(mapGalleryViewItem),
        myUploads: response.my_uploads.map(mapGalleryViewItem),
        albums: response.albums.map(mapGalleryAlbum),
        uploadConstraints: {
          maxFileSizeBytes: response.upload_constraints.max_file_size_bytes,
          acceptedMimePrefixes:
            response.upload_constraints.accepted_mime_prefixes,
        },
        pageInfo: {
          limit: response.page_info.limit,
          nextCursor: response.page_info.next_cursor,
        },
        favoriteItemIds: response.favorite_item_ids ?? [],
        pendingUploadSessions: (response.pending_upload_sessions ?? []).map(
          mapGalleryUploadSession,
        ),
      };
    },
    async setGalleryFavorite(
      occasionSlug: string,
      guestToken: string,
      galleryItemId: string,
      favorite: boolean,
    ): Promise<string[]> {
      const response = await httpClient.request<GuestGalleryFavoriteResponse>({
        method: favorite ? 'PUT' : 'DELETE',
        path: `/v1/guest/occasions/${occasionSlug}/gallery/favorites/${galleryItemId}`,
        query: {
          guest_token: guestToken,
        },
      });

      return response.favorite_item_ids;
    },
    async setGalleryReaction(
      occasionSlug: string,
      guestToken: string,
      galleryItemId: string,
      input: {
        reactionKind: 'heart' | 'celebrate';
        active: boolean;
      },
    ): Promise<ApiGalleryReactionSummary[]> {
      const response = await httpClient.request<GuestGalleryReactionResponse>({
        method: 'PUT',
        path: `/v1/guest/occasions/${occasionSlug}/gallery/reactions/${galleryItemId}`,
        query: {
          guest_token: guestToken,
        },
        body: {
          reaction_kind: input.reactionKind,
          active: input.active,
        },
      });

      return response.reactions;
    },
    async createGalleryComment(
      occasionSlug: string,
      guestToken: string,
      galleryItemId: string,
      body: string,
    ): Promise<ApiGalleryCommentRecord[]> {
      const response = await httpClient.request<GuestGalleryCommentResponse>({
        method: 'POST',
        path: `/v1/guest/occasions/${occasionSlug}/gallery/comments/${galleryItemId}`,
        query: {
          guest_token: guestToken,
        },
        body: {
          body,
        },
      });

      return response.comments;
    },
    async createGallerySharePackage(
      occasionSlug: string,
      guestToken: string,
      input: {
        albumId?: string | null;
        galleryItemIds?: string[];
      },
    ): Promise<ApiGallerySharePackageResponse> {
      return httpClient.request<ApiGallerySharePackageResponse>({
        method: 'POST',
        path: `/v1/guest/occasions/${occasionSlug}/gallery/share-packages`,
        query: {
          guest_token: guestToken,
        },
        body: {
          ...(input.albumId !== undefined ? { album_id: input.albumId } : {}),
          ...(input.galleryItemIds
            ? { gallery_item_ids: input.galleryItemIds }
            : {}),
        },
      });
    },
    async getGallerySharePackage(
      shareToken: string,
    ): Promise<ApiGallerySharePackageResponse> {
      return httpClient.request<ApiGallerySharePackageResponse>({
        method: 'GET',
        path: `/v1/gallery/share-packages/${shareToken}`,
      });
    },
    async requestGalleryUploadUrl(
      occasionSlug: string,
      guestToken: string,
      input: {
        eventId: string;
        albumId?: string | null;
        fileName: string;
        mimeType: string;
        fileSizeBytes?: number | null;
        checksumSha256?: string | null;
        caption?: string | null;
      },
    ): Promise<GuestGalleryUploadSession> {
      const body: GuestGalleryUploadRequest = {
        event_id: input.eventId,
        ...(input.albumId !== undefined ? { album_id: input.albumId } : {}),
        file_name: input.fileName,
        mime_type: input.mimeType,
        ...(input.fileSizeBytes !== undefined
          ? { file_size_bytes: input.fileSizeBytes }
          : {}),
        ...(input.checksumSha256 !== undefined
          ? { checksum_sha256: input.checksumSha256 }
          : {}),
        ...(input.caption !== undefined ? { caption: input.caption } : {}),
      };
      const response = await httpClient.request<RequestMediaUploadUrlResponse>({
        method: 'POST',
        path: `/v1/guest/occasions/${occasionSlug}/media/upload-url`,
        query: {
          ...galleryGuestTokenQuery(guestToken),
        },
        body,
      });

      return mapGalleryUploadSession(response.upload_session);
    },
    async completeGalleryUpload(
      occasionSlug: string,
      guestToken: string,
      uploadSessionId: string,
    ): Promise<GuestGalleryViewItem> {
      const body: CompleteMediaUploadRequest = {
        upload_session_id: uploadSessionId,
      };
      const response = await httpClient.request<
        GuestGalleryResponse['items'][number]
      >({
        method: 'POST',
        path: `/v1/guest/occasions/${occasionSlug}/media/complete`,
        query: {
          ...galleryGuestTokenQuery(guestToken),
        },
        body,
      });

      return mapGalleryViewItem(response);
    },
    async updateGalleryUpload(
      occasionSlug: string,
      guestToken: string,
      galleryItemId: string,
      input: {
        caption?: string | null;
      },
    ): Promise<GuestGalleryViewItem> {
      const body: GuestGalleryUploadUpdateRequest = {
        ...(input.caption !== undefined ? { caption: input.caption } : {}),
      };
      const response = await httpClient.request<
        GuestGalleryResponse['items'][number]
      >({
        method: 'PATCH',
        path: `/v1/guest/occasions/${occasionSlug}/gallery/uploads/${galleryItemId}`,
        query: {
          guest_token: guestToken,
        },
        body,
      });

      return mapGalleryViewItem(response);
    },
    async deleteGalleryUpload(
      occasionSlug: string,
      guestToken: string,
      galleryItemId: string,
    ): Promise<GuestGalleryUploadDeleteResponse> {
      return httpClient.request<GuestGalleryUploadDeleteResponse>({
        method: 'DELETE',
        path: `/v1/guest/occasions/${occasionSlug}/gallery/uploads/${galleryItemId}`,
        query: {
          guest_token: guestToken,
        },
      });
    },
    async uploadGalleryItem(
      occasionSlug: string,
      guestToken: string,
      input: {
        eventId: string;
        albumId?: string | null;
        fileName: string;
        mimeType: string;
        fileSizeBytes?: number | null;
        checksumSha256?: string | null;
        caption?: string | null;
      },
    ): Promise<GuestGalleryViewItem> {
      const body: GuestGalleryUploadRequest = {
        event_id: input.eventId,
        ...(input.albumId !== undefined ? { album_id: input.albumId } : {}),
        file_name: input.fileName,
        mime_type: input.mimeType,
        ...(input.fileSizeBytes !== undefined
          ? { file_size_bytes: input.fileSizeBytes }
          : {}),
        ...(input.checksumSha256 !== undefined
          ? { checksum_sha256: input.checksumSha256 }
          : {}),
        ...(input.caption !== undefined ? { caption: input.caption } : {}),
      };
      const session = await httpClient.request<RequestMediaUploadUrlResponse>({
        method: 'POST',
        path: `/v1/guest/occasions/${occasionSlug}/media/upload-url`,
        query: {
          ...galleryGuestTokenQuery(guestToken),
        },
        body,
      });

      const response = await httpClient.request<
        GuestGalleryResponse['items'][number]
      >({
        method: 'POST',
        path: `/v1/guest/occasions/${occasionSlug}/media/complete`,
        query: {
          ...galleryGuestTokenQuery(guestToken),
        },
        body: {
          upload_session_id: session.upload_session.id,
        } satisfies CompleteMediaUploadRequest,
      });

      return mapGalleryViewItem(response);
    },
    async submitForm(
      occasionSlug: string,
      guestToken: string,
      formId: string,
      input: FormSubmissionInput,
    ): Promise<FormSubmissionRecord> {
      const response = await httpClient.request<ApiFormSubmissionRecord>({
        method: 'POST',
        path: `/v1/guest/occasions/${occasionSlug}/forms/${formId}/submissions`,
        query: {
          guest_token: guestToken,
        },
        body: {
          answers: input.answers.map((answer) => ({
            field_id: answer.fieldId,
            value: answer.value,
          })),
        },
      });

      return mapFormSubmission(response);
    },
    async getRsvp(
      occasionSlug: string,
      guestToken: string,
    ): Promise<{
      invitation: RsvpInvitationRecord;
      publishableInvitation: GuestPublishableInvitation | null;
      unitDisplayName: string;
      participantDisplayNames: string[];
      identityContext: GuestIdentityContext;
      events: Array<
        RsvpGuestEventState & {
          linkedForm: GuestVisibleFormRecord['form'] | null;
          linkedFormSubmission: FormSubmissionRecord | null;
        }
      >;
    }> {
      const response = await httpClient.request<GuestRsvpResponse>({
        method: 'GET',
        path: `/v1/guest/occasions/${occasionSlug}/rsvp`,
        query: {
          guest_token: guestToken,
        },
      });

      return {
        invitation: mapRsvpInvitation(response.invitation),
        publishableInvitation: mapGuestPublishableInvitation(
          response.publishable_invitation,
        ),
        unitDisplayName: response.unit_display_name,
        participantDisplayNames: response.participant_display_names,
        identityContext: mapGuestIdentityContext(response.identity_context),
        events: response.events.map((event) => ({
          eventId: event.event_id,
          eventKey: event.event_key,
          displayName: event.display_name,
          guestSummary: event.guest_summary,
          startsAt: event.starts_at,
          locationName: event.location_name,
          linkedFormId: event.linked_form_id,
          deadlineAt: event.deadline_at,
          waitlistEnabled: event.waitlist_enabled,
          response: event.response ? mapRsvpResponse(event.response) : null,
          participantResponses: (event.participant_responses ?? []).map(
            (item) => ({
              participantActorId: item.participant_actor_id,
              participantDisplayName: item.participant_display_name,
              response: item.response ? mapRsvpResponse(item.response) : null,
            }),
          ),
          linkedForm: event.linked_form
            ? mapVisibleForm({
                form: event.linked_form,
                events: [],
                submission: null,
                submission_locked: false,
              }).form
            : null,
          linkedFormSubmission: event.linked_form_submission
            ? mapFormSubmission(event.linked_form_submission)
            : null,
        })),
      };
    },
    async getPublicInvitation(token: string) {
      const response = await httpClient.request<{
        invitation: ApiRsvpInvitationRecord;
        publishable_invitation: ApiGuestPublishableInvitation | null;
        occasion: {
          id: string;
          slug: string;
          display_name: string;
        };
        events: Array<{
          id: string;
          display_name: string;
          starts_at: string | null;
        }>;
        allow_add_others: boolean;
      }>({
        method: 'GET',
        path: `/v1/public/invitations/${token}`,
      });

      return {
        ...response,
        publishableInvitation: mapGuestPublishableInvitation(
          response.publishable_invitation,
        ),
      };
    },
    async getPublicDeliveryInvitation(deliveryToken: string) {
      const response = await httpClient.request<{
        invitation: ApiRsvpInvitationRecord;
        publishable_invitation: ApiGuestPublishableInvitation | null;
        occasion: {
          id: string;
          slug: string;
          display_name: string;
        };
        events: Array<{
          id: string;
          display_name: string;
          starts_at: string | null;
        }>;
        allow_add_others: boolean;
        delivery: {
          id: string;
          public_token: string | null;
          status: string;
          source: string;
        } | null;
      }>({
        method: 'GET',
        path: `/v1/public/deliveries/${deliveryToken}`,
      });

      return {
        ...response,
        publishableInvitation: mapGuestPublishableInvitation(
          response.publishable_invitation,
        ),
      };
    },
    async joinPublicInvitation(
      token: string,
      input: {
        firstName: string;
        lastName: string;
        email: string | null;
        phone: string | null;
      },
    ) {
      return httpClient.request<{
        contact: {
          id: string;
          display_name: string;
          first_name?: string | null;
          last_name?: string | null;
        };
        delivery: {
          id: string;
          public_token: string | null;
          status: string;
          source: string;
        };
        responses: unknown[];
      }>({
        method: 'POST',
        path: `/v1/public/invitations/${token}/join`,
        body: {
          first_name: input.firstName,
          last_name: input.lastName,
          email: input.email,
          phone: input.phone,
        },
      });
    },
    async addPersonFromDelivery(
      deliveryToken: string,
      input: {
        firstName: string;
        lastName: string;
        email: string | null;
        phone: string | null;
      },
    ) {
      return httpClient.request<{
        contact: {
          id: string;
          display_name: string;
          first_name?: string | null;
          last_name?: string | null;
        };
        delivery: {
          id: string;
          public_token: string | null;
          status: string;
          source: string;
        };
        responses: unknown[];
      }>({
        method: 'POST',
        path: `/v1/public/deliveries/${deliveryToken}/add-person`,
        body: {
          first_name: input.firstName,
          last_name: input.lastName,
          email: input.email,
          phone: input.phone,
        },
      });
    },
    async addPersonFromGuestRsvp(
      occasionSlug: string,
      guestToken: string,
      input: {
        firstName: string;
        lastName: string;
        email: string | null;
        phone: string | null;
      },
    ) {
      return httpClient.request({
        method: 'POST',
        path: `/v1/guest/occasions/${occasionSlug}/rsvp/add-person`,
        query: {
          guest_token: guestToken,
        },
        body: {
          first_name: input.firstName,
          last_name: input.lastName,
          email: input.email,
          phone: input.phone,
        },
      });
    },
    async submitRsvp(
      occasionSlug: string,
      guestToken: string,
      input: {
        events: Array<{
          eventId: string;
          participantActorId?: string | null;
          status: RsvpResponseStatus;
          note: string | null;
          linkedFormAnswers?: FormSubmissionInput['answers'];
        }>;
      },
    ): Promise<void> {
      await httpClient.request({
        method: 'POST',
        path: `/v1/guest/occasions/${occasionSlug}/rsvp`,
        query: {
          guest_token: guestToken,
        },
        body: {
          events: input.events.map((event) => ({
            event_id: event.eventId,
            participant_actor_id: event.participantActorId ?? null,
            status: event.status,
            note: event.note,
            linked_form_answers: event.linkedFormAnswers?.map((answer) => ({
              field_id: answer.fieldId,
              value: answer.value,
            })),
          })),
        },
      });
    },
  };
}
